import React, { useState, useEffect, useRef, useMemo } from 'react';
import DOMPurify from 'dompurify';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, Cell, ReferenceLine } from 'recharts';
import {
  CheckCircle, XCircle, Clock, Eye, TrendingUp, Calendar, TestTubes, Home, GitBranch, Play, TestTube, ChevronRight, ChevronDown, Search, ExternalLink, History, Loader2, AlertTriangle, RefreshCcw
} from 'lucide-react';
import azureDevOpsService from './services/azureDevOpsService';
import SkippedTestsPanel from './components/SkippedTestsPanel';
import TestResultsTable from './components/TestResultsTable';
import RobotReportButton from './components/RobotReportButton';
import SuiteVerifyButton from './components/SuiteVerifyButton';
import AllSuitesVerifiedBanner from './components/AllSuitesVerifiedBanner';
import { getStatusFromSuccessRate } from './config/statusConfig';
import { computeTestStats, computeSuccessRate, computeNetTestDelta, statusLabel, statusChartColor, isSkippedStatus, matchesStatusFilter } from './utils/testStatus';
import relaunchTracker from './services/relaunchTracker';
import { toast, Toaster } from 'react-hot-toast';
import elasticAuthService from './services/elasticAuthService';
import { useZoneDeTrace } from './services/traceZone';
import ElasticAuthModal from './ElasticAuthModal';
import AiAnalysisDrawer from './components/AiAnalysisDrawer';
import rootCauseService from './services/rootCauseService';
import bugLinkService from './services/bugLinkService';
import BatchRootCauseBar from './components/BatchRootCauseBar';
import SuiteAnalysisPanel from './components/SuiteAnalysisPanel';
import { analyzeSuite, getCachedSuiteAnalysis, writeSuiteAnalysisCache } from './services/aiSuiteAnalysisService';
import TestCaseSearchBar from './components/TestCaseSearchBar';

// Configuration Data Preparation
const DP_CONFIG = [
  {
    pipelineId: 609,
    pipelineName: "RF DP Browser",
    testSuiteName: "RF Data Preparation-browser",
    type: 'robotframework',
    testEnvironment: 'QA3'
  },
  {
    pipelineId: 608,
    pipelineName: "RF DP API",
    testSuiteName: "RF Data Preparation-api",
    type: 'robotframework',
    testEnvironment: 'QA3'
  },
  {
    pipelineId: 497,
    pipelineName: "DP Tests ReadyApi",
    type: 'readyapi',
    testEnvironment: 'QA3'
  }
];

const VERSION_CONFIGS_DP = {
  "26.2": [
    {
      "pipelineId": 641,
      "pipelineName": "RF DP Browser 26.2",
      "testSuiteName": "RF Data Preparation-browser",
      "type": "robotframework"
    },
    {
      "pipelineId": 640,
      "pipelineName": "RF DP API 26.2",
      "testSuiteName": "RF Data Preparation-api",
      "type": "robotframework"
    },
    {
      "pipelineId": 647,
      "pipelineName": "DP Tests ReadyApi 26.2",
      "type": "readyapi"
    }
  ],
  "26.1": [
    {
      "pipelineId": 609,
      "pipelineName": "RF DP Browser",
      "testSuiteName": "RF Data Preparation-browser",
      "type": "robotframework"
    },
    {
      "pipelineId": 608,
      "pipelineName": "RF DP API",
      "testSuiteName": "RF Data Preparation-api",
      "type": "robotframework"
    },
    {
      "pipelineId": 497,
      "pipelineName": "DP Tests ReadyApi",
      "type": "readyapi"
    }
  ],
  "25.2": [
    {
      "pipelineId": 526,
      "pipelineName": "RF Browser 25.2",
      "testSuiteName": "RF Data Preparation-browser",
      "testSuiteFilter": "test results dp",
      "type": "robotframework"
    },
    {
      "pipelineId": 525,
      "pipelineName": "RF API 25.2",
      "testSuiteName": "RF Data Preparation-api",
      "testSuiteFilter": "test results dp",
      "type": "robotframework"
    },
    {
      "pipelineId": 518,
      "pipelineName": "DP Tests ReadyApi 25.2",
      "type": "readyapi"
    }
  ],
  "25.1": [
    {
      "pipelineId": 389,
      "pipelineName": "DP Tests ReadyApi 25.1",
      "type": "readyapi"
    }
  ]
};

const READYAPI_PIPELINE_IDS = new Set([647, 497, 518, 389].map(String));

// Router interne
const useRouter = () => {
  const [currentPath, setCurrentPath] = useState('/');
  const [uiPath, setUiPath] = useState('/');
  const [params, setParams] = useState({});
  const baseRef = useRef('');

  const safeDecode = val => { try { return decodeURIComponent(val); } catch { return val; } };

  const parse = path => {
    const clean = path.split('?')[0] || '/';
    const parts = clean.split('/').filter(Boolean);
    const p = {};
    if (parts[0] === 'pipeline' && parts[1]) {
      p.pipelineId = parts[1];
      if (parts[2] === 'build' && parts[3]) {
        p.buildId = parts[3];
        if (parts[4] === 'run' && parts[5]) {
          p.runId = parts[5];
          if (parts[6] === 'testsuite' && parts[7]) {
            p.testSuiteName = safeDecode(parts.slice(7).join('/'));
          }
        }
      }
    } else if (parts[0] === 'testcase' && parts[1]) {
      p.testcaseName = safeDecode(parts[1]);
      if (parts[2] === 'history' && parts[3]) {
        p.pipelineId = parts[3];
        if (parts[4] === 'back') {
          p.backPipelineId = parts[5];
          if (parts[6] === 'build' && parts[7]) p.backBuildId = parts[7];
          if (parts[8] === 'run' && parts[9]) p.backRunId = parts[9];
          if (parts[10] === 'testsuite' && parts[11]) {
            p.backTestSuiteName = safeDecode(parts.slice(11).join('/'));
          }
        }
      }
    }
    return p;
  };

  const stripBase = path => {
    const base = baseRef.current;
    if (base && path.startsWith(base)) {
      const stripped = path.slice(base.length) || '/';
      return stripped.startsWith('/') ? stripped : `/${stripped}`;
    }
    return path || '/';
  };

  const applyPath = path => {
    const effective = stripBase(path);
    setCurrentPath(path);
    setUiPath(effective);
    setParams(parse(effective));
  };

  const navigate = path => {
    try {
      const full = `${baseRef.current || ''}${path}`;
      window.history.pushState({}, '', full);
    } catch {}
    applyPath(path);
  };

  useEffect(() => {
    const initialFull = (typeof window !== 'undefined' && window.location && (window.location.pathname + window.location.search)) || '/';
    try {
      const pathname = (typeof window !== 'undefined' && window.location && window.location.pathname) || '/';
      baseRef.current = pathname.startsWith('/datapreparation') ? '/datapreparation' : '';
    } catch { baseRef.current = ''; }
    applyPath(initialFull);
    const onPop = () => {
      const pFull = window.location.pathname + window.location.search;
      applyPath(pFull);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  return { currentPath, uiPath, params, navigate };
};

const StatusBadge = ({ status }) => {
  const styles = {
    succeeded: 'bg-green-100 text-green-800 border-green-300',
    success: 'bg-green-100 text-green-800 border-green-300',
    failed: 'bg-red-100 text-red-800 border-red-300',
    running: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    partiallysucceeded: 'bg-orange-100 text-orange-800 border-orange-300',
    passed: 'bg-green-100 text-green-800 border-green-300',
    skipped: 'bg-gray-100 text-gray-600 border-gray-300',
    notexecuted: 'bg-gray-100 text-gray-600 border-gray-300'
  };
  const icons = {
    success: <CheckCircle className="w-4 h-4" />,
    failed: <XCircle className="w-4 h-4" />,
    running: <Clock className="w-4 h-4" />,
    passed: <CheckCircle className="w-4 h-4" />,
    skipped: <Clock className="w-4 h-4" />,
    notexecuted: <Clock className="w-4 h-4" />
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.failed}`}>
      {icons[status]}
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const SuccessRateBadge = ({ successRate }) => {
  const normalizedRate = Number(String(successRate ?? '').replace('%', '')) || 0;
  const status = getStatusFromSuccessRate(normalizedRate, 0);
  const styles = {
    green: 'bg-green-100 text-green-800 border-green-300',
    yellow: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    red: 'bg-red-100 text-red-800 border-red-300'
  };
  const icons = {
    green: <CheckCircle className="w-4 h-4" />,
    yellow: <AlertTriangle className="w-4 h-4" />,
    red: <XCircle className="w-4 h-4" />
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${styles[status.color]}`}>
      {icons[status.color]}
      {status.label}
    </span>
  );
};

const formatDuration = seconds => {
  const minutes = Math.floor(seconds/60); const remainingSeconds = (seconds%60).toFixed(0); return `${minutes}m ${remainingSeconds}s`;
};
const formatDate = d => new Date(d).toLocaleString('fr-FR');

const Navigation = ({ currentPath, navigate }) => {
  const pathOnly = (currentPath||'/').split('?')[0];
  const pathParts = pathOnly.split('/').filter(Boolean);
  const breadcrumbs = [{ label:'Data Preparation', path:'/', icon:<Home className="w-4 h-4"/> }];
  if (pathParts[0] === 'pipeline' && pathParts[1]) {
    breadcrumbs.push({ label:pathParts[1], path:`/pipeline/${pathParts[1]}`, icon:<GitBranch className="w-4 h-4"/> });
    if (pathParts[2]==='build' && pathParts[3]) {
      breadcrumbs.push({ label:pathParts[3], path:`/pipeline/${pathParts[1]}/build/${pathParts[3]}`, icon:<Play className="w-4 h-4"/> });
      if (pathParts[4]==='run' && pathParts[5]) {
        breadcrumbs.push({ label:pathParts[5], path:`/pipeline/${pathParts[1]}/build/${pathParts[3]}/run/${pathParts[5]}`, icon:<TestTubes className="w-4 h-4"/> });
        if (pathParts[6]==='testsuite' && pathParts[7]) {
          const decodedTs = (()=>{ try { return decodeURIComponent(pathParts.slice(7).join('/')); } catch { return pathParts.slice(7).join('/'); } })();
          breadcrumbs.push({ label:decodedTs, path:`/pipeline/${pathParts[1]}/build/${pathParts[3]}/run/${pathParts[5]}/testsuite/${pathParts.slice(7).join('/')}`, icon:<TestTube className="w-4 h-4"/> });
        }
      }
    }
  }
  return <nav className="bg-white shadow-sm border-b mb-6"><div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"><div className="flex items-center space-x-2 py-3">{breadcrumbs.map((crumb,i)=>(<React.Fragment key={crumb.path}>{i>0 && <ChevronRight className="w-4 h-4 text-gray-400"/>}<button onClick={()=>navigate(crumb.path)} className="flex items-center space-x-1 text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors">{crumb.icon}<span>{crumb.label}</span></button></React.Fragment>))}</div></div></nav>;
};

// TestSuiteDetails RobotFramework
const RobotTestSuiteDetails = ({ pipelineId, buildId, runId, testSuiteName, squadName = 'Data Preparation', navigate, initialSearchTerm = '' }) => {
  const [testCases, setTestCases] = useState([]);
  const [relatedBugsMap, setRelatedBugsMap] = useState({});
  const [showBugsModal, setShowBugsModal] = useState(false);
  const [modalBugs, setModalBugs] = useState([]);
  const [modalBugTestName, setModalBugTestName] = useState(null);
  
  // Initialize nonReproMap from localStorage
  const [nonReproMap, setNonReproMap] = useState(() => {
    try {
      const storageKey = `dataprep:nonrepro:${pipelineId}:${runId}:${encodeURIComponent(testSuiteName)}`;
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const data = JSON.parse(raw);
        const today = new Date().toDateString();
        if (data.date === today && data.values) {
          return data.values;
        }
      }
    } catch (e) {
      console.warn('Failed to load nonReproMap from localStorage:', e);
    }
    return {};
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [statusFilter, setStatusFilter] = useState('all');
  const [subSuiteFilter, setSubSuiteFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [showAiAnalysis, setShowAiAnalysis] = useState(false);
  const [selectedAiTestCase, setSelectedAiTestCase] = useState(null);
  const [showSuiteAnalysis, setShowSuiteAnalysis] = useState(false);
  const [suiteAnalysisResult, setSuiteAnalysisResult] = useState(null);
  const [isSuiteAnalyzing, setIsSuiteAnalyzing] = useState(false);
  const [suiteAnalysisError, setSuiteAnalysisError] = useState(null);

  const handleAnalyzeSuite = async () => {
    setIsSuiteAnalyzing(true);
    setSuiteAnalysisError(null);
    try {
      const result = await analyzeSuite('Data Preparation', testCases, testCases.length);
      setSuiteAnalysisResult(result);
    } catch (err) {
      setSuiteAnalysisError(err.message);
    } finally {
      setIsSuiteAnalyzing(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const results = await azureDevOpsService.getTestCasesForRun(runId, 'robotframework');
        setTestCases(results);
      } catch (e) {
        console.error('Error getTestCasesForRun', e);
        setError('Erreur chargement tests');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [runId]);

  // Fetch related bugs for each test case (batched)
  useEffect(() => {
    let cancelled = false;
    if (!testCases || testCases.length === 0) {
      setRelatedBugsMap({});
      return;
    }

    const fetchRelatedBugs = async () => {
      const map = {};
      const concurrency = 5;
      for (let i = 0; i < testCases.length && !cancelled; i += concurrency) {
        const batch = testCases.slice(i, i + concurrency);
        try {
          const promises = batch.map(tc =>
            azureDevOpsService.getRelatedBugsForTestCaseByTitle(tc.name).catch(err => {
              console.warn('Erreur getRelatedBugs for', tc.name, err);
              return [];
            })
          );
          const results = await Promise.all(promises);
          batch.forEach((tc, idx) => {
            map[tc.name] = results[idx] || [];
          });
        } catch (err) {
          console.error('Erreur batch fetching related bugs:', err);
        }
      }

      try {
        const localMap = await bugLinkService.getForBuild(pipelineId, buildId);
        Object.entries(localMap).forEach(([testName, bugs]) => {
          const existing = map[testName] || [];
          const merged = [...existing];
          bugs.forEach(b => { if (!merged.some(m => m.id === b.id)) merged.push({ ...b, local: true }); });
          map[testName] = merged;
        });
      } catch (err) {
        console.warn('Erreur récupération bugs liés locaux:', err);
      }

      if (!cancelled) setRelatedBugsMap(map);
    };

    fetchRelatedBugs();
    return () => { cancelled = true; };
  }, [testCases, pipelineId, buildId]);

  // Persist nonReproMap to localStorage
  useEffect(() => {
    try {
      const storageKey = `dataprep:nonrepro:${pipelineId}:${runId}:${encodeURIComponent(testSuiteName)}`;
      const today = new Date().toDateString();
      const data = { date: today, values: nonReproMap };
      localStorage.setItem(storageKey, JSON.stringify(data));
    } catch (e) {
      console.warn('Failed to save nonReproMap to localStorage:', e);
    }
  }, [nonReproMap, pipelineId, runId, testSuiteName]);

  const [nonReproLoading, setNonReproLoading] = useState(new Set());

  const markNonRepro = async (testName, testResultId) => {
    if (nonReproMap[testName] || nonReproLoading.has(testName)) return;
    setNonReproLoading(prev => new Set([...prev, testName]));
    try {
      await azureDevOpsService.updateTestResultOutcome(runId, testResultId);
      setNonReproMap(prev => ({ ...prev, [testName]: true }));
      setTestCases(prev => prev.map(t => t.name === testName ? { ...t, status: 'passed' } : t));
    } catch (err) {
      console.error('Erreur mise à jour AzDO non-reproduit:', err);
      setNonReproMap(prev => ({ ...prev, [testName]: true }));
      setTestCases(prev => prev.map(t => t.name === testName ? { ...t, status: 'passed' } : t));
    } finally {
      setNonReproLoading(prev => { const n = new Set(prev); n.delete(testName); return n; });
    }
  };

  const [rootCauseMap, setRootCauseMap] = useState({});
  const [selectedTests, setSelectedTests] = useState(new Set());
  const [notesMap, setNotesMap] = useState({});
  useEffect(() => {
    if (!buildId) return;
    rootCauseService.getForBuild(pipelineId, buildId)
      .then(({ rootCauses, notes }) => { setRootCauseMap(rootCauses); setNotesMap(notes); })
      .catch(err => console.warn('Failed to load root causes:', err));
  }, [pipelineId, buildId]);
  const handleRootCause = async (testName, cause) => {
    const prev = rootCauseMap[testName];
    setRootCauseMap(m => ({ ...m, [testName]: cause }));
    try {
      if (cause) {
        await rootCauseService.set({ pipelineId, buildId, runId, testName, rootCause: cause, squad: squadName });
      } else {
        await rootCauseService.remove({ pipelineId, buildId, testName });
      }
    } catch (err) {
      console.error('Failed to save root cause:', err);
      setRootCauseMap(m => ({ ...m, [testName]: prev }));
    }
  };

  const handleBatchRootCause = async (cause) => {
    const names = [...selectedTests];
    const prev = { ...rootCauseMap };
    setRootCauseMap(m => {
      const next = { ...m };
      if (cause) names.forEach(nm => { next[nm] = cause; });
      else names.forEach(nm => { delete next[nm]; });
      return next;
    });
    try {
      await Promise.all(names.map(nm =>
        cause
          ? rootCauseService.set({ pipelineId, buildId, runId, testName: nm, rootCause: cause, squad: squadName })
          : rootCauseService.remove({ pipelineId, buildId, testName: nm })
      ));
    } catch (err) {
      console.error('Failed to save batch root cause:', err);
      setRootCauseMap(prev);
    }
    setSelectedTests(new Set());
  };

  const handleNoteSave = async (testName, notes) => {
    try {
      await rootCauseService.setNotes({ pipelineId, buildId, testName, notes });
    } catch (err) {
      console.error('Failed to save note:', err);
    }
  };

  const [linkBugStatus, setLinkBugStatus] = useState(null);

  const linkOneBug = async (testName, bugId) => {
    const summary = await azureDevOpsService.getWorkItemSummary(bugId);
    if ((summary.type || '').toLowerCase() !== 'bug') {
      throw new Error(`#${summary.id} n'est pas un Bug (${summary.type || 'type inconnu'})`);
    }
    let linked = summary;
    try {
      await azureDevOpsService.linkBugToTestCaseByTitle(testName, summary.id);
    } catch (err) {
      if (!/Test Case introuvable/.test(err.message || '')) throw err;
      // Pas de Test Case ADO pour ce test: on garde le lien côté app uniquement
      await bugLinkService.add({ pipelineId, buildId, testName, bug: summary });
      linked = { ...summary, local: true };
    }
    setRelatedBugsMap(prev => {
      const existing = prev[testName] || [];
      if (existing.some(b => b.id === linked.id)) return prev;
      return { ...prev, [testName]: [...existing, linked] };
    });
  };

  const handleBatchLinkBug = async (bugId) => {
    const summary = await azureDevOpsService.getWorkItemSummary(bugId);
    if ((summary.type || '').toLowerCase() !== 'bug') {
      setLinkBugStatus({ type: 'error', message: `#${summary.id} n'est pas un Bug (${summary.type || 'type inconnu'})` });
      return;
    }

    const names = [...selectedTests].filter(
      nm => !(relatedBugsMap[nm] || []).some(b => b.id === summary.id)
    );

    if (names.length === 0) {
      setLinkBugStatus({ type: 'error', message: 'Tests déjà liés à ce bug' });
      return;
    }

    const linkedByTest = {};
    const failed = [];
    let localCount = 0;

    await Promise.all(names.map(async (nm) => {
      try {
        await azureDevOpsService.linkBugToTestCaseByTitle(nm, summary.id);
        linkedByTest[nm] = summary;
      } catch (err) {
        if (!/Test Case introuvable/.test(err.message || '')) {
          failed.push(nm);
          return;
        }
        try {
          await bugLinkService.add({ pipelineId, buildId, testName: nm, bug: summary });
          linkedByTest[nm] = { ...summary, local: true };
          localCount++;
        } catch {
          failed.push(nm);
        }
      }
    }));

    setRelatedBugsMap(prev => {
      const next = { ...prev };
      Object.entries(linkedByTest).forEach(([nm, bug]) => {
        const existing = next[nm] || [];
        next[nm] = existing.some(b => b.id === bug.id) ? existing : [...existing, bug];
      });
      return next;
    });

    const successCount = Object.keys(linkedByTest).length;
    setLinkBugStatus(
      failed.length > 0
        ? { type: 'error', message: `${successCount} test(s) lié(s), ${failed.length} échec(s)` }
        : { type: 'success', message: `${successCount} test(s) lié(s) au bug #${summary.id}${localCount ? ` (${localCount} en local, hors ADO)` : ''}` }
    );
    setSelectedTests(new Set());
  };

  const getSubSuite = (test) => test.automatedTestStorage ? test.automatedTestStorage.split('.').pop() : null;

  const subSuiteOptions = [...new Set(testCases.map(getSubSuite).filter(Boolean))].sort((a, b) => a.localeCompare(b));

  const filtered = testCases
    .filter(t => (t.name.toLowerCase().includes(searchTerm.toLowerCase())) && matchesStatusFilter(t.status, statusFilter) && (subSuiteFilter === 'all' || getSubSuite(t) === subSuiteFilter))
    .sort((a, b) => {
      if (sortBy === 'duration') return (b.duration || 0) - (a.duration || 0);
      if (sortBy === 'status') return a.status.localeCompare(b.status);
      if (sortBy === 'subsuite') return (getSubSuite(a) || '').localeCompare(getSubSuite(b) || '');
      return a.name.localeCompare(b.name);
    });

  const { total, passed, failed, skipped, successRate } = computeTestStats(testCases);

  if (loading) return <div className="flex justify-center items-center h-64">Chargement...</div>;
  if (error) return <div className="text-red-600 text-center">{error}</div>;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Suite: {testSuiteName}</h2>
            <p className="text-gray-600">Build: {buildId} | Run: {runId}</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/')} className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700">
              ← Retour
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-4">
          {[
            { label: 'Total Tests', value: total, color: 'text-gray-900' },
            { label: 'Passés', value: passed, color: 'text-green-600' },
            { label: 'Échoués', value: failed, color: 'text-red-600' },
            { label: 'Ignorés', value: skipped, color: 'text-gray-500' },
            { label: 'Taux Réussite', value: successRate + '%', color: 'text-blue-600' }
          ].map(k => (
            <div key={k.label} className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">{k.label}</span>
                <span className={`text-lg font-bold ${k.color}`}>{k.value}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <div className="px-6 py-4">
            <h3 className="text-lg font-semibold text-gray-900">Tests</h3>
          </div>
        </div>
        <div className="p-6 space-y-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Rechercher..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-2 border rounded-md">
                <option value="all">Tous</option>
                <option value="passed">Passés</option>
                <option value="failed">Échoués</option>
                <option value="skipped">Ignorés</option>
              </select>
              <select value={subSuiteFilter} onChange={e => setSubSuiteFilter(e.target.value)} className="px-3 py-2 border rounded-md">
                <option value="all">Toutes les suites</option>
                {subSuiteOptions.map(subSuite => (
                  <option key={subSuite} value={subSuite}>{subSuite}</option>
                ))}
              </select>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="px-3 py-2 border rounded-md">
                <option value="name">Nom</option>
                <option value="status">Statut</option>
                <option value="duration">Durée</option>
                <option value="subsuite">Suite</option>
              </select>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b bg-gray-50">
              <h3 className="text-lg font-semibold">Tests Filtrés ({filtered.length})</h3>
            </div>
            <TestResultsTable
              tests={filtered}
              columns={{ suite: true, rootCause: true }}
              StatusBadge={StatusBadge}
              selectedTests={selectedTests}
              onSelectedTestsChange={setSelectedTests}
              relatedBugsMap={relatedBugsMap}
              nonReproMap={nonReproMap}
              nonReproLoading={nonReproLoading}
              onMarkNonRepro={markNonRepro}
              onLinkBug={linkOneBug}
              onShowBugs={(bugs, testName) => { setModalBugs(bugs); setModalBugTestName(testName); setShowBugsModal(true); }}
              rootCauseMap={rootCauseMap}
              notesMap={notesMap}
              onRootCause={handleRootCause}
              onNoteChange={(testName, val) => setNotesMap(m => ({ ...m, [testName]: val }))}
              onNoteBlur={handleNoteSave}
              getSubSuite={getSubSuite}
              renderActions={t => (
                <>
                  {t.logUrl && (
                    <a href={t.logUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" />Log
                    </a>
                  )}
                  {t.status === 'failed' && (
                    <button
                      onClick={() => {
                        setSelectedAiTestCase(t);
                        setShowAiAnalysis(true);
                      }}
                      className="text-purple-600 hover:text-purple-800"
                      title={`Analyser ${t.name} avec l'IA`}
                    >
                      Analyser
                    </button>
                  )}
                  <button
                    onClick={() => navigate(`/testcase/${encodeURIComponent(t.name)}/history/${pipelineId}/back/${pipelineId}/build/${buildId}/run/${runId}/testsuite/${encodeURIComponent(testSuiteName)}`)}
                    className="text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                    title="Voir l'historique"
                  >
                    <History className="w-3 h-3" />Historique
                  </button>
                </>
              )}
              emptyState={(
                <div className="text-center py-12">
                  <TestTube className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun test</h3>
                  <p className="text-sm text-gray-500">Ajustez vos filtres.</p>
                </div>
              )}
            />
          </div>
        </div>
      </div>

      <BatchRootCauseBar
        count={selectedTests.size}
        onApply={handleBatchRootCause}
        onClear={() => { setSelectedTests(new Set()); setLinkBugStatus(null); }}
        onLinkBug={handleBatchLinkBug}
        linkBugStatus={linkBugStatus}
      />
      <AiAnalysisDrawer
        open={showAiAnalysis}
        onClose={() => {
          setShowAiAnalysis(false);
          setSelectedAiTestCase(null);
        }}
        testCase={selectedAiTestCase}
        context={{
          source: 'data-preparation-robotframework',
          pipelineId,
          buildId,
          runId,
          testSuiteName
        }}
      />
      <SuiteAnalysisPanel
        open={showSuiteAnalysis}
        onClose={() => setShowSuiteAnalysis(false)}
        squadName="Data Preparation"
        failedCount={failed}
        isLoading={isSuiteAnalyzing}
        error={suiteAnalysisError}
        result={suiteAnalysisResult}
        onAnalyze={handleAnalyzeSuite}
      />
      <BugsModal
        show={showBugsModal}
        bugs={modalBugs}
        testName={modalBugTestName}
        onClose={() => setShowBugsModal(false)}
      />
    </div>
  );
};

// Historique Test Case RobotFramework
const RobotTestCaseHistory = ({ testcaseName, pipelineId, backPipelineId, backBuildId, backRunId, backTestSuiteName, navigate }) => {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await azureDevOpsService.getAutomatedTestHistory(testcaseName, pipelineId);
        const correctedData = (data || []).map((item, index) => {
          const fullTimestamp = item.dateTimestamp || item.completedDate || item.startedDate || Date.now() - (index * 24 * 60 * 60 * 1000);
          const dateObj = new Date(fullTimestamp);
          return {
            ...item,
            dateTimestamp: fullTimestamp,
            date: item.date || dateObj.toISOString().split('T')[0],
            dateTime: dateObj.toISOString(),
            value: 1,
            statusDisplay: statusLabel(item.status)
          };
        })
        .sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime));
        setHistoryData(correctedData);
      } catch (e) {
        console.error('Erreur chargement historique test:', e);
        setError("Impossible de charger l'historique");
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [testcaseName, pipelineId]);

  const filteredData = historyData.filter(item => {
    const matchStatus = matchesStatusFilter(item.status, statusFilter);
    const matchDate = !dateFilter || item.date === dateFilter;
    return matchStatus && matchDate;
  });

  const { total: totalTests, failed: failedTests, skipped: skippedTests, successRate } = computeTestStats(filteredData);
  const avgDuration = totalTests > 0 ? Math.round(filteredData.reduce((sum, d) => sum + (d.durationSeconds || d.duration || 0), 0) / totalTests) : 0;

  if (loading) return <div className="text-center py-8">Chargement de l'historique...</div>;
  if (error) return <div className="text-center py-8 text-red-600">{error}</div>;
  if (historyData.length === 0) return <div className="text-center py-8 text-gray-500">Aucun historique trouvé pour ce test</div>;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Historique du Test</h2>
            <p className="text-gray-600">{testcaseName}</p>
          </div>
          <button 
            onClick={() => {
              if (backPipelineId && backBuildId && backRunId && backTestSuiteName) {
                const ts = encodeURIComponent(backTestSuiteName);
                navigate(`/pipeline/${backPipelineId}/build/${backBuildId}/run/${backRunId}/testsuite/${ts}`);
              } else {
                navigate(`/`);
              }
            }} 
            className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition-colors">
            ← Retour
          </button>
        </div>

        <div className="mt-4 flex flex-wrap gap-4">
          <select className="border px-2 py-1 rounded" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">Tous les statuts</option>
            <option value="passed">Passé</option>
            <option value="failed">Échoué</option>
            <option value="skipped">Ignoré</option>
          </select>
          <input type="date" className="border px-2 py-1 rounded" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} />
          <button className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300" onClick={() => { setStatusFilter('all'); setDateFilter(''); }}>Réinitialiser</button>
        </div>
      </div>

      <div className="grid grid-cols-5 gap-4">
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">{totalTests}</div>
          <div className="text-sm text-gray-600">Exécutions</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-green-600">{successRate}%</div>
          <div className="text-sm text-gray-600">Taux de réussite</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-red-600">{failedTests}</div>
          <div className="text-sm text-gray-600">Échecs</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-gray-500">{skippedTests}</div>
          <div className="text-sm text-gray-600">Ignorés</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">{avgDuration}ms</div>
          <div className="text-sm text-gray-600">Durée moyenne</div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Historique des Statuts d'Exécution</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={filteredData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis dataKey="date" tick={{ fontSize: 12 }} angle={-45} textAnchor="end" height={80} />
            <YAxis domain={[0, 1]} tick={false} label={{ value: 'Statut', angle: -90, position: 'insideLeft' }} />
            <Tooltip formatter={(value, name, props) => [props.payload.statusDisplay, 'Statut']} labelFormatter={(label) => `Date: ${label}`} />
            <Bar dataKey="value" name="Statut d'exécution">
              {filteredData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={statusChartColor(entry.status)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Évolution de la Durée</h3>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={filteredData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis dataKey="date" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} label={{ value: 'Durée (s)', angle: -90, position: 'insideLeft' }} />
            <Tooltip formatter={(value) => [`${value}s`, 'Durée']} labelFormatter={(label) => `Date: ${label}`} />
            <Area type="monotone" dataKey="durationSeconds" stroke="#3b82f6" strokeWidth={2} fill="url(#colorDuration)" name="Durée d'exécution" />
            <defs>
              <linearGradient id="colorDuration" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
              </linearGradient>
            </defs>
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Détails par Build</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Build</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Durée</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredData.map((entry, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(entry.dateTime).toLocaleString('fr-FR', { 
                      year: 'numeric', 
                      month: '2-digit', 
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{entry.buildId}</td>
                  <td className="px-6 py-4 whitespace-nowrap"><StatusBadge status={entry.status} /></td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{entry.duration}s</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {entry.logUrl && (
                      <a href={entry.logUrl} className="text-blue-600 hover:text-blue-800 flex items-center gap-1" target="_blank" rel="noreferrer">
                        <ExternalLink className="w-3 h-3" />
                        Voir Log
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Modal pour afficher les bugs liés
const BugsModal = ({ show, bugs, testName, onClose }) => {
  if (!show) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Bugs liés: {testName}</h3>
            <p className="text-sm text-gray-500">{(bugs || []).length} bug(s) trouvés</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XCircle className="w-6 h-6" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto flex-1 space-y-3">
          {(!bugs || bugs.length === 0) ? (
            <div className="text-gray-600 text-center py-8">Aucun bug lié trouvé.</div>
          ) : (
            bugs.map(b => (
              <div key={b.id} className="border rounded p-3 flex justify-between items-start hover:bg-gray-50 transition-colors">
                <div className="flex-1">
                  <a href={b.url} target="_blank" rel="noreferrer" className="text-gray-700 font-medium hover:underline">#{b.id} — {b.title}</a>
                  <div className="text-sm text-gray-600 mt-1">Etat: {b.state}</div>
                </div>
                <div className="text-right text-sm ml-4">
                  <a href={b.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline">Ouvrir <ExternalLink className="w-4 h-4"/></a>
                </div>
              </div>
            ))
          )}
        </div>
        <div className="flex items-center justify-end p-4 border-t bg-gray-50">
          <button onClick={onClose} className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700">Fermer</button>
        </div>
      </div>
    </div>
  );
};

// Modal pour afficher les logs complets d'un test ReadyAPI
const LogsModal = ({ show, testCase, fullLogContent, fullLogLoading, onClose, onTraceIdClick }) => {
  if (!show || !testCase) return null;
  
  const getStatusIcon = (status) => {
    if (status === 'passed') return <CheckCircle className="w-5 h-5 text-green-600" />;
    if (status === 'failed') return <XCircle className="w-5 h-5 text-red-600" />;
    return <Clock className="w-5 h-5 text-gray-400" />;
  };

  const TRACE_REGEX = /(X-Trace-Id:\s*\[([0-9a-fA-F\-]{16,64})\])/g;
  const renderWithTraceLinks = (text) => {
    if (!text) return null;
    const parts = [];
    let lastIndex = 0;
    let match;
    while ((match = TRACE_REGEX.exec(text)) !== null) {
      const full = match[1];
      const id = match[2];
      const start = match.index;
      if (start > lastIndex) {
        parts.push(text.slice(lastIndex, start));
      }
      parts.push(
        <button
          key={`trace-${start}`}
          className="underline text-blue-600 hover:text-blue-800"
          onClick={() => onTraceIdClick && onTraceIdClick(id)}
          title={`Chercher TraceId ${id}`}
        >
          {full}
        </button>
      );
      lastIndex = start + full.length;
    }
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }
    return parts;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Logs: {testCase?.name}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XCircle className="w-6 h-6" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto flex-1 space-y-6">
          <div className="border rounded-lg p-4 shadow-sm">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                {getStatusIcon(testCase.status)}
                <span className="font-semibold">{testCase.name}</span>
                {testCase.isNewFailure && (
                  <span className="ml-2 text-xs font-medium px-2 py-1 bg-red-100 text-red-700 rounded">Nouveau</span>
                )}
              </div>
              {testCase.logUrl && (
                <a
                  href={testCase.logUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 text-sm hover:underline flex items-center gap-1"
                >
                  Voir sur Azure DevOps <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
            {testCase.error && (
              <div className="mb-2 text-sm text-red-600 whitespace-pre-wrap">
                {testCase.error}
              </div>
            )}
            
            {fullLogLoading && (
              <div className="flex items-center gap-2 text-blue-600 py-4">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Chargement du log complet depuis les attachements...</span>
              </div>
            )}
            
            {!fullLogLoading && fullLogContent && (
              <div className="bg-gray-50 p-3 rounded">
                <div className="text-xs font-semibold text-green-700 mb-2 flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" />
                  Log complet récupéré depuis l'attachement
                </div>
                <pre className="text-xs whitespace-pre-wrap font-mono overflow-auto max-h-[500px] bg-white p-3 rounded border">
                  {renderWithTraceLinks(fullLogContent)}
                </pre>
              </div>
            )}
            
            {!fullLogLoading && !fullLogContent && testCase.stackTrace && (
              <>
                <div className="text-xs font-semibold text-orange-700 mb-2 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" />
                  Log partiel (stackTrace limité en caractères)
                </div>
                <pre className="text-xs whitespace-pre-wrap font-mono overflow-auto max-h-[500px] bg-white p-3 rounded border">
                  {renderWithTraceLinks(DOMPurify.sanitize(String(testCase.stackTrace)))}
                </pre>
              </>
            )}
            
            {!fullLogLoading && !fullLogContent && !testCase.stackTrace && (
              <div className="text-xs text-gray-500 italic">Aucun log disponible.</div>
            )}
          </div>
        </div>
        <div className="p-4 border-t flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded text-sm text-gray-700"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

// Modal to display system logs by TraceId
const TraceLogsModal = ({ show, traceId, loading, result, error, onClose }) => {
  if (!show || !traceId) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-base font-semibold text-gray-900">Logs applicatifs pour TraceId {traceId}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XCircle className="w-6 h-6" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          {loading && (
            <div className="flex items-center gap-2 text-blue-600">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Recherche des logs applicatifs…</span>
            </div>
          )}
          {!loading && error && (
            <div className="text-red-600 text-sm">{String(error)}</div>
          )}
          {!loading && !error && (
            <pre className="text-xs whitespace-pre-wrap font-mono overflow-auto max-h-[70vh] bg-gray-50 p-3 rounded border">
              {typeof result === 'string' ? result : JSON.stringify(result, null, 2)}
            </pre>
          )}
        </div>
        <div className="p-3 border-t flex justify-end">
          <button onClick={onClose} className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded text-sm text-gray-700">Fermer</button>
        </div>
      </div>
    </div>
  );
};

// Composant pour afficher les détails d'une test suite ReadyAPI
const ReadyApiTestSuiteDetails = ({ pipelineId, buildId, runId, testSuiteName, squadName = 'Data Preparation', navigate, initialSearchTerm = '' }) => {
  const [testCases, setTestCases] = useState([]);
  const [relatedBugsMap, setRelatedBugsMap] = useState({});
  const [showBugsModal, setShowBugsModal] = useState(false);
  const [modalBugs, setModalBugs] = useState([]);
  const [modalBugTestName, setModalBugTestName] = useState(null);
  
  // Initialize nonReproMap from localStorage
  const [nonReproMap, setNonReproMap] = useState(() => {
    try {
      const storageKey = `dataprep:readyapi:nonrepro:${pipelineId}:${runId}:${encodeURIComponent(testSuiteName)}`;
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const data = JSON.parse(raw);
        const today = new Date().toDateString();
        if (data.date === today && data.values) {
          return data.values;
        }
      }
    } catch (e) {
      console.warn('Failed to load nonReproMap from localStorage:', e);
    }
    return {};
  });
  
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [runningTests, setRunningTests] = useState({});
  const [showTestCaseModal, setShowTestCaseModal] = useState(false);
  const [selectedTestCaseName, setSelectedTestCaseName] = useState(null);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [selectedLogsTestCase, setSelectedLogsTestCase] = useState(null);
  const [showAiAnalysis, setShowAiAnalysis] = useState(false);
  const [selectedAiTestCase, setSelectedAiTestCase] = useState(null);
  const [fullLogContent, setFullLogContent] = useState(null);
  const [fullLogLoading, setFullLogLoading] = useState(false);
  const [logInput, setLogInput] = useState('');
  const [expandedItems, setExpandedItems] = useState(new Set());
  // Trace logs modal state
  const [showTraceModal, setShowTraceModal] = useState(false);
  const [selectedTraceId, setSelectedTraceId] = useState(null);
  const [traceLoading, setTraceLoading] = useState(false);
  const [traceResult, setTraceResult] = useState(null);
  const [traceError, setTraceError] = useState(null);
  // La zone suit l'environnement du build ; 'qa3' n'est plus qu'un repli
  // pour un build qui n'en declare aucun (cf. services/traceZone.js).
  const traceZone = useZoneDeTrace(buildId, 'qa3');
  // Elastic authentication state
  const [showElasticAuthModal, setShowElasticAuthModal] = useState(false);
  const [pendingTraceId, setPendingTraceId] = useState(null);
  
  // Gestion du relaunch tracker
  const normalizeSuiteKey = (s) => {
    try { return decodeURIComponent(String(s || '')).trim().toLowerCase(); } catch { return String(s || '').trim().toLowerCase(); }
  };
  const suiteKey = React.useMemo(() => normalizeSuiteKey(testSuiteName), [testSuiteName]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const results = await azureDevOpsService.getTestCasesForRun(runId, 'develop');
        setTestCases(results);
      } catch (e) {
        console.error('Error getTestCasesForRun', e);
        setError('Erreur chargement tests');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [runId]);

  // Fetch related bugs for each test case (batched)
  useEffect(() => {
    let cancelled = false;
    if (!testCases || testCases.length === 0) {
      setRelatedBugsMap({});
      return;
    }

    const fetchRelatedBugs = async () => {
      const map = {};
      const concurrency = 5;
      for (let i = 0; i < testCases.length && !cancelled; i += concurrency) {
        const batch = testCases.slice(i, i + concurrency);
        try {
          const promises = batch.map(tc =>
            azureDevOpsService.getRelatedBugsForTestCaseByTitle(tc.name).catch(err => {
              console.warn('Erreur getRelatedBugs for', tc.name, err);
              return [];
            })
          );
          const results = await Promise.all(promises);
          batch.forEach((tc, idx) => {
            map[tc.name] = results[idx] || [];
          });
        } catch (err) {
          console.error('Erreur batch fetching related bugs:', err);
        }
      }

      try {
        const localMap = await bugLinkService.getForBuild(pipelineId, buildId);
        Object.entries(localMap).forEach(([testName, bugs]) => {
          const existing = map[testName] || [];
          const merged = [...existing];
          bugs.forEach(b => { if (!merged.some(m => m.id === b.id)) merged.push({ ...b, local: true }); });
          map[testName] = merged;
        });
      } catch (err) {
        console.warn('Erreur récupération bugs liés locaux:', err);
      }

      if (!cancelled) setRelatedBugsMap(map);
    };

    fetchRelatedBugs();
    return () => { cancelled = true; };
  }, [testCases, pipelineId, buildId]);

  // Persist nonReproMap to localStorage
  useEffect(() => {
    try {
      const storageKey = `dataprep:readyapi:nonrepro:${pipelineId}:${runId}:${encodeURIComponent(testSuiteName)}`;
      const today = new Date().toDateString();
      const data = { date: today, values: nonReproMap };
      localStorage.setItem(storageKey, JSON.stringify(data));
    } catch (e) {
      console.warn('Failed to save nonReproMap to localStorage:', e);
    }
  }, [nonReproMap, pipelineId, runId, testSuiteName]);

  const [nonReproLoading, setNonReproLoading] = useState(new Set());

  const markNonRepro = async (testName, testResultId) => {
    if (nonReproMap[testName] || nonReproLoading.has(testName)) return;
    setNonReproLoading(prev => new Set([...prev, testName]));
    try {
      await azureDevOpsService.updateTestResultOutcome(runId, testResultId);
      setNonReproMap(prev => ({ ...prev, [testName]: true }));
      setTestCases(prev => prev.map(t => t.name === testName ? { ...t, status: 'passed' } : t));
    } catch (err) {
      console.error('Erreur mise à jour AzDO non-reproduit:', err);
      setNonReproMap(prev => ({ ...prev, [testName]: true }));
      setTestCases(prev => prev.map(t => t.name === testName ? { ...t, status: 'passed' } : t));
    } finally {
      setNonReproLoading(prev => { const n = new Set(prev); n.delete(testName); return n; });
    }
  };

  const [rootCauseMap, setRootCauseMap] = useState({});
  const [selectedTests, setSelectedTests] = useState(new Set());
  const [notesMap, setNotesMap] = useState({});
  useEffect(() => {
    if (!buildId) return;
    rootCauseService.getForBuild(pipelineId, buildId)
      .then(({ rootCauses, notes }) => { setRootCauseMap(rootCauses); setNotesMap(notes); })
      .catch(err => console.warn('Failed to load root causes:', err));
  }, [pipelineId, buildId]);
  const handleRootCause = async (testName, cause) => {
    const prev = rootCauseMap[testName];
    setRootCauseMap(m => ({ ...m, [testName]: cause }));
    try {
      if (cause) {
        await rootCauseService.set({ pipelineId, buildId, runId, testName, rootCause: cause, squad: squadName });
      } else {
        await rootCauseService.remove({ pipelineId, buildId, testName });
      }
    } catch (err) {
      console.error('Failed to save root cause:', err);
      setRootCauseMap(m => ({ ...m, [testName]: prev }));
    }
  };

  const handleBatchRootCause = async (cause) => {
    const names = [...selectedTests];
    const prev = { ...rootCauseMap };
    setRootCauseMap(m => {
      const next = { ...m };
      if (cause) names.forEach(nm => { next[nm] = cause; });
      else names.forEach(nm => { delete next[nm]; });
      return next;
    });
    try {
      await Promise.all(names.map(nm =>
        cause
          ? rootCauseService.set({ pipelineId, buildId, runId, testName: nm, rootCause: cause, squad: squadName })
          : rootCauseService.remove({ pipelineId, buildId, testName: nm })
      ));
    } catch (err) {
      console.error('Failed to save batch root cause:', err);
      setRootCauseMap(prev);
    }
    setSelectedTests(new Set());
  };

  const handleNoteSave = async (testName, notes) => {
    try {
      await rootCauseService.setNotes({ pipelineId, buildId, testName, notes });
    } catch (err) {
      console.error('Failed to save note:', err);
    }
  };

  const [linkBugStatus, setLinkBugStatus] = useState(null);

  const linkOneBug = async (testName, bugId) => {
    const summary = await azureDevOpsService.getWorkItemSummary(bugId);
    if ((summary.type || '').toLowerCase() !== 'bug') {
      throw new Error(`#${summary.id} n'est pas un Bug (${summary.type || 'type inconnu'})`);
    }
    let linked = summary;
    try {
      await azureDevOpsService.linkBugToTestCaseByTitle(testName, summary.id);
    } catch (err) {
      if (!/Test Case introuvable/.test(err.message || '')) throw err;
      // Pas de Test Case ADO pour ce test: on garde le lien côté app uniquement
      await bugLinkService.add({ pipelineId, buildId, testName, bug: summary });
      linked = { ...summary, local: true };
    }
    setRelatedBugsMap(prev => {
      const existing = prev[testName] || [];
      if (existing.some(b => b.id === linked.id)) return prev;
      return { ...prev, [testName]: [...existing, linked] };
    });
  };

  const handleBatchLinkBug = async (bugId) => {
    const summary = await azureDevOpsService.getWorkItemSummary(bugId);
    if ((summary.type || '').toLowerCase() !== 'bug') {
      setLinkBugStatus({ type: 'error', message: `#${summary.id} n'est pas un Bug (${summary.type || 'type inconnu'})` });
      return;
    }

    const names = [...selectedTests].filter(
      nm => !(relatedBugsMap[nm] || []).some(b => b.id === summary.id)
    );

    if (names.length === 0) {
      setLinkBugStatus({ type: 'error', message: 'Tests déjà liés à ce bug' });
      return;
    }

    const linkedByTest = {};
    const failed = [];
    let localCount = 0;

    await Promise.all(names.map(async (nm) => {
      try {
        await azureDevOpsService.linkBugToTestCaseByTitle(nm, summary.id);
        linkedByTest[nm] = summary;
      } catch (err) {
        if (!/Test Case introuvable/.test(err.message || '')) {
          failed.push(nm);
          return;
        }
        try {
          await bugLinkService.add({ pipelineId, buildId, testName: nm, bug: summary });
          linkedByTest[nm] = { ...summary, local: true };
          localCount++;
        } catch {
          failed.push(nm);
        }
      }
    }));

    setRelatedBugsMap(prev => {
      const next = { ...prev };
      Object.entries(linkedByTest).forEach(([nm, bug]) => {
        const existing = next[nm] || [];
        next[nm] = existing.some(b => b.id === bug.id) ? existing : [...existing, bug];
      });
      return next;
    });

    const successCount = Object.keys(linkedByTest).length;
    setLinkBugStatus(
      failed.length > 0
        ? { type: 'error', message: `${successCount} test(s) lié(s), ${failed.length} échec(s)` }
        : { type: 'success', message: `${successCount} test(s) lié(s) au bug #${summary.id}${localCount ? ` (${localCount} en local, hors ADO)` : ''}` }
    );
    setSelectedTests(new Set());
  };

  // Tracker subscription for relaunch status
  useEffect(() => {
    relaunchTracker.start();
    setRunningTests(relaunchTracker.getEntriesFor('develop', suiteKey));
    const unsub = relaunchTracker.subscribe(() => {
      setRunningTests(relaunchTracker.getEntriesFor('develop', suiteKey));
    });
    return () => unsub();
  }, [suiteKey]);

  // Helper function to get step name from test suite name
  const getStepNameFromTestSuite = (testSuiteName) => {
    switch (testSuiteName) {
      case 'DP - smoke tests':
        return 'Run Smoke Tests';
      case 'DP - Regression Test':
        return 'Run Regression Test';
      case 'DP - Absimilia Test Processus':
        return 'Run Absimilia Test Suite';
      case 'DP - Clean Up DP':
        return 'Run Clean Up Test Suite';
      case 'DE - Cleanup':
        return 'Run Cleanup';
      case 'DE - smoke tests':
        return 'Run Smoke Tests';
      case 'DE - Regression Tests Explo':
        return 'Run Regression Suite';
      case 'DE - Admin':
        return 'Run  Suite Admin Explo';
      case 'Framework - smoke tests':
        return 'Run Smoke Tests';
      default:
        return testSuiteName;
    }
  };

  // Fetch des logs (version fonctionnelle originale)
  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setLogsLoading(true);
        const stepName = getStepNameFromTestSuite(testSuiteName);
        const log = await azureDevOpsService.getTestLogsFromBuild(buildId, stepName);
        if (log && log[0] && log[0].content) {
          setLogInput(log[0].content);
        } else {
          setLogInput('');
        }
      } catch (err) {
        console.error('Erreur lors de la récupération des logs :', err);
        setError(err.message);
      } finally {
        setLogsLoading(false);
      }
    };

    fetchLogs();
  }, [buildId, testSuiteName]);

  // Fonction pour extraire les logs d'un test case spécifique
  const extractTestCaseLog = (fullLogs, testCaseName) => {
    const startPattern = new RegExp(`Running TestCase \\[${testCaseName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\]`);
    const endPattern = new RegExp(`Finished running TestCase \\[${testCaseName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\]`);
    
    const lines = fullLogs.split('\n');
    let inside = false;
    const extracted = [];
    
    for (const line of lines) {
      if (startPattern.test(line)) {
        inside = true;
      }
      if (inside) {
        extracted.push(line);
      }
      if (endPattern.test(line)) {
        break;
      }
    }
    
    if (extracted.length === 0) {
      return `Aucun log trouvé pour le test case: ${testCaseName}`;
    }
    
    return extracted.join('\n');
  };

  const openTraceModal = async (traceId) => {
    try {
      // Check if credentials exist
      const credentials = elasticAuthService.getCredentials(traceZone);
      if (!credentials) {
        setPendingTraceId(traceId);
        setShowElasticAuthModal(true);
        return;
      }
      
      setSelectedTraceId(traceId);
      setShowTraceModal(true);
      setTraceLoading(true);
      setTraceResult(null);
      setTraceError(null);
      const resp = await fetch('/api/logs/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ traceId, zone: traceZone, auth: credentials.token })
      });
      const contentType = resp.headers.get('content-type') || '';
      let data;
      if (contentType.includes('application/json')) {
        data = await resp.json();
      } else {
        data = await resp.text();
      }
      if (!resp.ok) {
        // Si 401, credentials invalides -> les supprimer et redemander
        if (resp.status === 401) {
          elasticAuthService.clearCredentials(traceZone);
          setPendingTraceId(traceId);
          setShowElasticAuthModal(true);
          setTraceError('Authentification échouée. Veuillez vérifier vos credentials.');
          return;
        }
        throw new Error(typeof data === 'string' ? data : (data.error || 'Erreur upstream'));
      }
      setTraceResult(data);
    } catch (e) {
      setTraceError(e.message || String(e));
    } finally {
      setTraceLoading(false);
    }
  };

  // Handle Elastic authentication
  const handleElasticAuth = async (username, password) => {
    elasticAuthService.saveCredentials(traceZone, username, password);
    setShowElasticAuthModal(false);
    setTraceError(null); // Réinitialiser l'erreur
    if (pendingTraceId) {
      await openTraceModal(pendingTraceId);
      setPendingTraceId(null);
    }
  };

  const parseReadyAPILogs = (logs) => {
    const lines = logs.split('\n').filter(line => line.trim());
    const testSuites = [];
    let currentSuite = null;
    let currentTestCase = null;
    let currentStep = null;
    let testCaseStack = [];

    const extractTime = (line) => {
      const match = line.match(/^(\d{2}:\d{2}:\d{2},\d{3})/);
      return match ? match[1] : '';
    };

    const toMs = (t) => {
      const m = t.match(/^(\d{2}):(\d{2}):(\d{2}),(\d{3})$/);
      if (!m) return null;
      const [_, hh, mm, ss, ms] = m;
      return (+hh) * 3600000 + (+mm) * 60000 + (+ss) * 1000 + (+ms);
    };

    const normalizeAssertStatus = (s) => {
      if (!s) return s;
      const up = String(s).toUpperCase();
      if (up === 'VALID' || up === 'PASSED') return 'PASS';
      if (up === 'INVALID' || up === 'FAILED') return 'FAIL';
      return up;
    };

    const updateStepStatuses = (testCase) => {
      testCase.steps.forEach(step => {
        if (step.nestedTestCases) {
          step.nestedTestCases.forEach(nestedTC => updateStepStatuses(nestedTC));
          const hasFailedNestedTC = step.nestedTestCases.some(ntc => ntc.status === 'FAIL');
          if (hasFailedNestedTC) {
            step.status = 'FAIL';
            return;
          }
        }
        if (step.assertions.length > 0) {
          step.status = step.assertions.some(a => a.status === 'FAIL') ? 'FAIL' : 'PASS';
        } else if (step.errorDetails && step.errorDetails.length > 0) {
          step.status = 'FAIL';
        } else {
          step.status = 'PASS';
        }
      });
      const hasFailedStep = testCase.steps.some(s => s.status === 'FAIL');
      if (hasFailedStep && testCase.status !== 'FAIL') {
        testCase.status = 'FAIL';
      }
    };

    lines.forEach(line => {
      if (!line.includes('[SoapUIProTestCaseRunner]')) return;

      if (line.includes('Running Test Suite')) {
        const match = line.match(/Running Test Suite \[([^\]]+)\]/);
        if (match) {
          currentSuite = { name: match[1], status: 'RUNNING', testCases: [], startTime: extractTime(line) };
          testSuites.push(currentSuite);
          testCaseStack = [];
        }
      }
      else if (line.includes('Running TestCase')) {
        const match = line.match(/Running TestCase \[([^\]]+)\]/);
        if (match && currentSuite) {
          const testCaseData = { name: match[1], status: 'RUNNING', steps: [], startTime: extractTime(line), isNested: testCaseStack.length > 0 };
          if (testCaseStack.length === 0) {
            currentSuite.testCases.push(testCaseData);
            currentTestCase = testCaseData;
          } else {
            if (currentStep) {
              if (!currentStep.nestedTestCases) currentStep.nestedTestCases = [];
              currentStep.nestedTestCases.push(testCaseData);
            }
          }
          testCaseStack.push(testCaseData);
        }
      }
      else if (line.includes('running step')) {
        const match = line.match(/running step \[([^\]]+)\]/);
        if (match) {
          const nowT = extractTime(line);
          if (currentStep && currentStep.startTime) {
            currentStep.endTime = nowT;
            const d = toMs(nowT) - toMs(currentStep.startTime);
            if (!isNaN(d)) currentStep.durationMs = Math.max(0, d);
          }
          const stepData = { name: match[1], status: 'RUNNING', assertions: [], startTime: nowT };
          const activeTestCase = testCaseStack[testCaseStack.length - 1];
          if (activeTestCase) {
            activeTestCase.steps.push(stepData);
            currentStep = stepData;
          }
        }
      }
      else if (line.includes('Assertion') && line.includes('has status')) {
        const match = line.match(/Assertion \[([^\]]+)\] has status\s+([A-Z]+)/i);
        if (match && currentStep) {
          const assertion = { name: match[1], status: normalizeAssertStatus(match[2]), time: extractTime(line) };
          currentStep.assertions.push(assertion);
          if (assertion.status === 'FAIL' && currentStep.statusCode) {
            assertion.httpStatusCode = currentStep.statusCode;
          }
        }
      }
      else if (line.includes('ASSERTION FAILED')) {
        const errorMatch = line.match(/ASSERTION FAILED -> (.+)/);
        if (errorMatch && currentStep) {
          const errorMsg = errorMatch[1];
          const lastAssertion = currentStep.assertions[currentStep.assertions.length - 1];
          if (lastAssertion && lastAssertion.status === 'FAIL') {
            lastAssertion.errorMessage = errorMsg;
          } else {
            if (!currentStep.errorDetails) currentStep.errorDetails = [];
            currentStep.errorDetails.push({ time: extractTime(line), message: errorMsg });
          }
        }
      }
      else if (line.includes('Finished running TestCase')) {
        const match = line.match(/Finished running TestCase \[([^\]]+)\].*?time taken:?\s*(\d+)\s*ms.*?status:\s*(PASS|FAIL|FAILED|VALID|INVALID)/i);
        const altMatch = line.match(/Finished running TestCase \[([^\]]+)\].*status:\s*([A-Z]+)/i);
        if (match || altMatch) {
          const testCaseName = (match || altMatch)[1];
          const statusRaw = (match || altMatch)[(match ? 3 : 2)];
          const durationMs = match ? Number(match[2]) : undefined;
          const status = normalizeAssertStatus(statusRaw);
          const endT = extractTime(line);
          if (currentStep && currentStep.startTime) {
            currentStep.endTime = endT;
            const d = toMs(endT) - toMs(currentStep.startTime);
            if (!isNaN(d)) currentStep.durationMs = Math.max(0, d);
          }
          const finishedTestCase = testCaseStack.find(tc => tc.name === testCaseName);
          if (finishedTestCase) {
            updateStepStatuses(finishedTestCase);
            if (status === 'FAIL' || finishedTestCase.status === 'FAIL') {
              finishedTestCase.status = 'FAIL';
            } else {
              finishedTestCase.status = status;
            }
            finishedTestCase.endTime = endT;
            if (durationMs !== undefined) finishedTestCase.durationMs = durationMs;
            testCaseStack = testCaseStack.filter(tc => tc.name !== testCaseName);
            if (testCaseStack.length === 0) {
              currentTestCase = null;
              currentStep = null;
            } else {
              currentTestCase = testCaseStack[0];
            }
          }
        }
      }
    });

    testSuites.forEach(suite => {
      suite.testCases.forEach(tc => updateStepStatuses(tc));
      const failedTestCases = suite.testCases.filter(tc => tc.status === 'FAIL');
      if (failedTestCases.length > 0) {
        suite.status = 'FAIL';
      }
    });

    return testSuites;
  };

  const toggleExpanded = (path) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(path)) {
      newExpanded.delete(path);
    } else {
      newExpanded.add(path);
    }
    setExpandedItems(newExpanded);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PASS': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'FAIL': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'RUNNING': return <Clock className="w-4 h-4 text-yellow-500" />;
      default: return <Play className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PASS': return 'text-green-700 bg-green-50 border-green-200';
      case 'FAIL': return 'text-red-700 bg-red-50 border-red-200';
      case 'RUNNING': return 'text-yellow-700 bg-yellow-50 border-yellow-200';
      default: return 'text-gray-700 bg-gray-50 border-gray-200';
    }
  };

  const renderTestCase = (testCase, path, level = 0) => {
    const isExpanded = expandedItems.has(path);
    const hasChildren = testCase.steps && testCase.steps.length > 0;
    
    return (
      <div key={path} className={`${level > 0 ? 'ml-6 mt-2' : 'mt-4'}`}>
        <div
          className={`flex items-center p-3 rounded-lg border cursor-pointer hover:shadow-sm transition-shadow ${getStatusColor(testCase.status)}`}
          onClick={() => hasChildren && toggleExpanded(path)}
        >
          {hasChildren && (
            <span className="mr-2">
              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </span>
          )}
          {getStatusIcon(testCase.status)}
          <span className="ml-2 font-medium">
            {testCase.isNested ? '↳ ' : ''}{testCase.name}
          </span>
          <span className="ml-auto text-sm text-gray-500 flex items-center gap-3">
            {testCase.startTime && <span>Début: {testCase.startTime}</span>}
            {typeof testCase.durationMs === 'number' && (
              <span>Durée: {Math.round(testCase.durationMs)} ms</span>
            )}
          </span>
        </div>
        
        {isExpanded && hasChildren && (
          <div className="mt-2">
            {testCase.steps.map((step, stepIndex) => renderStep(step, `${path}-step-${stepIndex}`, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const renderStep = (step, path, level = 0) => {
    const isExpanded = expandedItems.has(path);
    const hasChildren = (step.assertions && step.assertions.length > 0) || (step.nestedTestCases && step.nestedTestCases.length > 0) || (step.errorDetails && step.errorDetails.length > 0);
    const hasHttpError = step.statusCode && (step.statusCode >= 400 || step.status === 'FAIL');
    
    return (
      <div key={path} className={`ml-${level * 4} mt-2`}>
        <div className={`flex items-center p-2 rounded border cursor-pointer hover:bg-gray-50 ${getStatusColor(step.status)}`} onClick={() => hasChildren && toggleExpanded(path)}>
          {hasChildren && <span className="mr-2">{isExpanded ? '▼' : '▶'}</span>}
          {getStatusIcon(step.status)}
          <span className="ml-2 flex-1">{step.name}</span>
          <span className="ml-auto text-xs text-gray-500 flex items-center gap-3">
            {step.startTime && <span>Début: {step.startTime}</span>}
            {typeof step.durationMs === 'number' && <span>Durée: {Math.round(step.durationMs)} ms</span>}
            {typeof step.responseTimeMs === 'number' && <span>Resp: {Math.round(step.responseTimeMs)} ms</span>}
            {typeof step.statusCode === 'number' && <span className={hasHttpError ? 'font-bold text-red-600' : ''}>HTTP: {step.statusCode}</span>}
          </span>
        </div>
        
        {isExpanded && hasChildren && (
          <div className="mt-1">
            {/* Nested Test Cases */}
            {step.nestedTestCases && step.nestedTestCases.map((nestedTC, index) => 
              renderTestCase(nestedTC, `${path}-nested-${index}`, level + 1)
            )}
            
            {/* Step Error Details (critiques, affichés en premier) */}
            {step.errorDetails && step.errorDetails.length > 0 && (
              <div className="ml-8 mt-1 p-3 rounded border-2 border-red-300 bg-red-50 text-sm">
                <strong className="text-red-800 flex items-center gap-2">
                  <XCircle className="w-4 h-4" />
                  Erreurs détectées:
                </strong>
                {step.errorDetails.map((err, errIndex) => (
                  <div key={errIndex} className="mt-2 text-red-700">
                    <div className="flex items-start gap-2">
                      <span className="text-gray-500 text-xs">{err.time}</span>
                      <div className="flex-1">
                        <div className="font-medium">{err.message}</div>
                        {err.httpStatusCode && (
                          <div className="mt-1 text-xs">
                            <span className="px-2 py-1 bg-red-200 rounded font-bold">
                              HTTP {err.httpStatusCode}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Assertions */}
            {step.assertions && step.assertions.map((assertion, assertionIndex) => (
              <div key={`${path}-assertion-${assertionIndex}`} className={`ml-8 mt-1 p-2 rounded border text-sm ${getStatusColor(assertion.status)}`}>
                <div className="flex items-center">
                  {getStatusIcon(assertion.status)}
                  <span className="ml-2 font-medium">{assertion.name}</span>
                  <span className="ml-auto flex items-center gap-2">
                    {assertion.httpStatusCode && (
                      <span className={`text-xs font-bold px-2 py-0.5 rounded ${assertion.status === 'FAIL' ? 'bg-red-200 text-red-800' : 'bg-green-100 text-green-700'}`}>
                        HTTP {assertion.httpStatusCode}
                      </span>
                    )}
                    {assertion.time && (
                      <span className="text-xs text-gray-500">{assertion.time}</span>
                    )}
                  </span>
                </div>
                {assertion.errorMessage && (
                  <div className="mt-2 text-xs text-red-600 bg-red-100 p-2 rounded">
                    <strong>Erreur:</strong> {assertion.errorMessage}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };
  
  const handleRelancer = async (testName) => {
    toast.loading(`Relance du test ${testName} en cours...`, { id: testName });
    
    const result = await azureDevOpsService.launchTestCase(testName, testSuiteName);
    if (result.success) {
      toast.success(`Test ${testName} relancé`, { id: testName });
      setRunningTests(prev => {
        const next = {
          ...prev,
          [testName]: {
            status: 'inProgress',
            runId: result.runId,
            runUrl: result.runUrl,
            pipelineId: result.pipelineId,
            startedAt: new Date().toISOString(),
            result: null,
          }
        };
        relaunchTracker.upsertRelaunch('develop', suiteKey, testName, next[testName]);
        return next;
      });
    } else {
      toast.error(`Erreur lors du déclenchement du test`, { id: testName });
    }
  };
  
  const getRelanceStatus = (testName) => {
    const relance = runningTests[testName];
    if (!relance) return null;

    if (relance.status === 'inProgress') {
      return (
        <a 
          href={relance.runUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          className="flex items-center space-x-2"
        >
          <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
          <span className="text-sm text-yellow-600 font-medium">En cours...</span>
          <ExternalLink className="w-3 h-3" />
        </a>
      );
    }

    if (relance.status === 'completed') {
      if (relance.result === 'succeeded') {
        return (
          <a 
            href={relance.runUrl} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="flex items-center space-x-2 text-green-600 hover:text-green-800 hover:underline transition-colors"
          >
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-sm font-medium">Succès</span>
            <ExternalLink className="w-3 h-3" />
          </a>
        );
      } else {
        return (
          <a 
            href={relance.runUrl} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="flex items-center space-x-2 text-red-600 hover:text-red-800 hover:underline transition-colors"
          >
            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
            <span className="text-sm font-medium">Échec</span>
            <ExternalLink className="w-3 h-3" />
          </a>
        );
      }
    }

    return <span className="text-sm text-gray-400">-</span>;
  };

  const filtered = testCases.filter(t => 
    (t.name.toLowerCase().includes(searchTerm.toLowerCase())) && 
    matchesStatusFilter(t.status, statusFilter)
  ).sort((a, b) => {
    if (sortBy === 'duration') return (b.duration || 0) - (a.duration || 0);
    if (sortBy === 'status') return a.status.localeCompare(b.status);
    return a.name.localeCompare(b.name);
  });

  const { total, passed, failed, skipped, successRate } = computeTestStats(testCases);

  if (loading) return <div className="flex justify-center items-center h-64">Chargement...</div>;
  if (error) return <div className="text-red-600 text-center">{error}</div>;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Suite: {testSuiteName}</h2>
            <p className="text-gray-600">Build: {buildId} | Run: {runId}</p>
          </div>
          <button onClick={() => navigate(`/pipeline/${pipelineId}`)} className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700">
            ← Retour
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-4">
          {[
            { label: 'Total Tests', value: total, color: 'text-gray-900' },
            { label: 'Passés', value: passed, color: 'text-green-600' },
            { label: 'Échoués', value: failed, color: 'text-red-600' },
            { label: 'Ignorés', value: skipped, color: 'text-gray-500' },
            { label: 'Taux Réussite', value: successRate + '%', color: 'text-blue-600' }
          ].map(k => (
            <div key={k.label} className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">{k.label}</span>
                <span className={`text-lg font-bold ${k.color}`}>{k.value}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <div className="px-6 py-4">
            <h3 className="text-lg font-semibold text-gray-900">Tests</h3>
          </div>
        </div>
        <div className="p-6 space-y-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Rechercher..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-2 border rounded-md">
                <option value="all">Tous</option>
                <option value="passed">Passés</option>
                <option value="failed">Échoués</option>
                <option value="skipped">Ignorés</option>
              </select>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="px-3 py-2 border rounded-md">
                <option value="name">Nom</option>
                <option value="status">Statut</option>
                <option value="duration">Durée</option>
              </select>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b bg-gray-50">
              <h3 className="text-lg font-semibold">Tests Filtrés ({filtered.length})</h3>
            </div>
            <TestResultsTable
              tests={filtered}
              columns={{ rootCause: true, relance: true }}
              StatusBadge={StatusBadge}
              selectedTests={selectedTests}
              onSelectedTestsChange={setSelectedTests}
              relatedBugsMap={relatedBugsMap}
              nonReproMap={nonReproMap}
              nonReproLoading={nonReproLoading}
              onMarkNonRepro={markNonRepro}
              onLinkBug={linkOneBug}
              onShowBugs={(bugs, testName) => { setModalBugs(bugs); setModalBugTestName(testName); setShowBugsModal(true); }}
              rootCauseMap={rootCauseMap}
              notesMap={notesMap}
              onRootCause={handleRootCause}
              onNoteChange={(testName, val) => setNotesMap(m => ({ ...m, [testName]: val }))}
              onNoteBlur={handleNoteSave}
              getRelanceStatus={getRelanceStatus}
              renderName={t => (
                <div className="flex items-center gap-2">
                  {t.isNewFailure && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-orange-100 text-orange-800 border border-orange-300">
                      New
                    </span>
                  )}
                  <button
                    onClick={() => {
                      setSelectedTestCaseName(t.name);
                      setShowTestCaseModal(true);
                    }}
                    className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline text-left"
                  >
                    {t.name}
                  </button>
                </div>
              )}
              renderActions={t => (
                <>
                  {t.logUrl && (
                    <button
                      onClick={async () => {
                        setSelectedLogsTestCase(t);
                        setShowLogsModal(true);
                        setFullLogContent(null);
                        setFullLogLoading(true);
                        try {
                          const logContent = await azureDevOpsService.getFullTestCaseLog(t);
                          setFullLogContent(logContent);
                        } catch (err) {
                          console.error('Erreur récupération log complet:', err);
                        } finally {
                          setFullLogLoading(false);
                        }
                      }}
                      className="text-blue-600 hover:text-blue-800"
                      title={`Afficher les logs de ${t.name}`}
                    >
                      Logs
                    </button>
                  )}
                  {t.status === 'failed' && (
                    <button
                      onClick={() => {
                        setSelectedAiTestCase(t);
                        setShowAiAnalysis(true);
                      }}
                      className="text-purple-600 hover:text-purple-800"
                      title={`Analyser ${t.name} avec l'IA`}
                    >
                      Analyser
                    </button>
                  )}
                  <button
                    onClick={() => handleRelancer(t.name)}
                    className="text-blue-600 hover:text-blue-800"
                    disabled={runningTests[t.name]?.status === 'inProgress'}
                  >
                    {runningTests[t.name]?.status === 'inProgress' ? 'Relance...' : 'Relancer'}
                  </button>
                  <button
                    onClick={() => navigate(`/testcase/${encodeURIComponent(t.name)}/history/${pipelineId}/back/${pipelineId}/build/${buildId}/run/${runId}/testsuite/${encodeURIComponent(testSuiteName)}`)}
                    className="text-blue-600 hover:text-blue-800"
                    title="Voir l'historique"
                  >
                    Historique
                  </button>
                </>
              )}
              emptyState={(
                <div className="text-center py-12">
                  <TestTube className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun test</h3>
                  <p className="text-sm text-gray-500">Ajustez vos filtres.</p>
                </div>
              )}
            />
          </div>
        </div>
      </div>

      {/* Modal pour afficher les détails d'un test case */}
      {showTestCaseModal && selectedTestCaseName && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowTestCaseModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Détails: {selectedTestCaseName}</h3>
              <button onClick={() => setShowTestCaseModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {(() => {
                const testCaseLog = extractTestCaseLog(logInput, selectedTestCaseName);
                const wrappedLog = `[SoapUIProTestCaseRunner] Running Test Suite [${testSuiteName}]\n${testCaseLog}\n[SoapUIProTestCaseRunner] Finished running Test Suite [${testSuiteName}]`;
                const parsedData = parseReadyAPILogs(wrappedLog);
                
                if (parsedData.length > 0 && parsedData[0].testCases.length > 0) {
                  const testCase = parsedData[0].testCases[0];
                  return (
                    <div className="space-y-4">
                      <div className={`p-4 rounded-lg border ${getStatusColor(testCase.status)}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {getStatusIcon(testCase.status)}
                            <span className="font-semibold text-lg">{testCase.name}</span>
                          </div>
                          <div className="text-sm text-gray-600 flex gap-4">
                            {testCase.startTime && <span>Début: {testCase.startTime}</span>}
                            {typeof testCase.durationMs === 'number' && <span>Durée: {Math.round(testCase.durationMs)} ms</span>}
                          </div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        {testCase.steps && testCase.steps.length > 0 ? (
                          testCase.steps.map((step, stepIndex) => renderStep(step, `modal-step-${stepIndex}`, 0))
                        ) : (
                          <div className="text-center py-4 text-gray-500">Aucun step trouvé pour ce test case.</div>
                        )}
                      </div>
                    </div>
                  );
                }
                return (
                  <div className="text-center py-8 text-gray-500">
                    <p>Aucun détail trouvé pour ce test case.</p>
                    <pre className="mt-4 text-xs text-left bg-gray-100 p-4 rounded overflow-auto max-h-60">{testCaseLog}</pre>
                  </div>
                );
              })()}
            </div>
            <div className="flex items-center justify-end p-4 border-t bg-gray-50">
              <button onClick={() => setShowTestCaseModal(false)} className="bg-gray-600 text-white px-6 py-2 rounded-md hover:bg-gray-700 transition-colors">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      <ElasticAuthModal
        isOpen={showElasticAuthModal}
        onClose={() => { setShowElasticAuthModal(false); setPendingTraceId(null); }}
        onAuthenticate={handleElasticAuth}
        zone={traceZone}
      />
      <BugsModal
        show={showBugsModal}
        bugs={modalBugs}
        testName={modalBugTestName}
        onClose={() => setShowBugsModal(false)}
      />
      <LogsModal 
        show={showLogsModal} 
        testCase={selectedLogsTestCase} 
        fullLogContent={fullLogContent} 
        fullLogLoading={fullLogLoading} 
        onClose={() => { setShowLogsModal(false); setSelectedLogsTestCase(null); setFullLogContent(null); }} 
        onTraceIdClick={openTraceModal}
      />
      <TraceLogsModal
        show={showTraceModal}
        traceId={selectedTraceId}
        loading={traceLoading}
        result={traceResult}
        error={traceError}
        onClose={() => { setShowTraceModal(false); setSelectedTraceId(null); setTraceResult(null); setTraceError(null); }}
      />
      <BatchRootCauseBar
        count={selectedTests.size}
        onApply={handleBatchRootCause}
        onClear={() => { setSelectedTests(new Set()); setLinkBugStatus(null); }}
        onLinkBug={handleBatchLinkBug}
        linkBugStatus={linkBugStatus}
      />
      <AiAnalysisDrawer
        open={showAiAnalysis}
        onClose={() => {
          setShowAiAnalysis(false);
          setSelectedAiTestCase(null);
        }}
        testCase={selectedAiTestCase}
        context={{
          source: 'data-preparation-readyapi',
          pipelineId,
          buildId,
          runId,
          testSuiteName
        }}
      />
    </div>
  );
};

// Historique Test Case ReadyAPI
const ReadyApiTestCaseHistory = ({ testcaseName, pipelineId, backPipelineId, backBuildId, backRunId, backTestSuiteName, navigate }) => {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await azureDevOpsService.getAutomatedTestHistory(testcaseName, pipelineId);
        const correctedData = (data || []).map((item, index) => {
          const fullTimestamp = item.dateTimestamp || item.completedDate || item.startedDate || Date.now() - (index * 24 * 60 * 60 * 1000);
          const dateObj = new Date(fullTimestamp);
          return {
            ...item,
            dateTimestamp: fullTimestamp,
            date: item.date || dateObj.toISOString().split('T')[0],
            dateTime: dateObj.toISOString(),
            value: 1,
            statusDisplay: statusLabel(item.status)
          };
        }).sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime));
        setHistoryData(correctedData);
      } catch (e) {
        console.error('Erreur chargement historique test:', e);
        setError("Impossible de charger l'historique");
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [testcaseName, pipelineId]);

  const filteredData = historyData.filter(item => {
    const matchStatus = matchesStatusFilter(item.status, statusFilter);
    const matchDate = !dateFilter || item.date === dateFilter;
    return matchStatus && matchDate;
  });

  const { total: totalTests, failed: failedTests, skipped: skippedTests, successRate } = computeTestStats(filteredData);
  const avgDuration = totalTests > 0 ? Math.round(filteredData.reduce((sum, d) => sum + (d.durationSeconds || d.duration || 0), 0) / totalTests) : 0;

  if (loading) return <div className="text-center py-8">Chargement de l'historique...</div>;
  if (error) return <div className="text-center py-8 text-red-600">{error}</div>;
  if (historyData.length === 0) return <div className="text-center py-8 text-gray-500">Aucun historique trouvé pour ce test</div>;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Historique du Test</h2>
            <p className="text-gray-600">{testcaseName}</p>
          </div>
          <button
            onClick={() => {
              if (backPipelineId && backBuildId && backRunId && backTestSuiteName) {
                const ts = encodeURIComponent(backTestSuiteName);
                navigate(`/pipeline/${backPipelineId}/build/${backBuildId}/run/${backRunId}/testsuite/${ts}`);
              } else {
                navigate(`/`);
              }
            }}
            className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition-colors"
          >
            ← Retour
          </button>
        </div>

        <div className="mt-4 flex flex-wrap gap-4">
          <select className="border px-2 py-1 rounded" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">Tous les statuts</option>
            <option value="passed">Passé</option>
            <option value="failed">Échoué</option>
            <option value="skipped">Ignoré</option>
          </select>
          <input type="date" className="border px-2 py-1 rounded" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} />
          <button className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300" onClick={() => { setStatusFilter('all'); setDateFilter(''); }}>
            Réinitialiser
          </button>
        </div>
      </div>

      <div className="grid grid-cols-5 gap-4">
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">{totalTests}</div>
          <div className="text-sm text-gray-600">Exécutions</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-green-600">{successRate}%</div>
          <div className="text-sm text-gray-600">Taux de réussite</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-red-600">{failedTests}</div>
          <div className="text-sm text-gray-600">Échecs</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-gray-500">{skippedTests}</div>
          <div className="text-sm text-gray-600">Ignorés</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">{avgDuration}ms</div>
          <div className="text-sm text-gray-600">Durée moyenne</div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Historique des Statuts d'Exécution</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={filteredData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis dataKey="date" tick={{ fontSize: 12 }} angle={-45} textAnchor="end" height={80} />
            <YAxis domain={[0, 1]} tick={false} label={{ value: 'Statut', angle: -90, position: 'insideLeft' }} />
            <Tooltip formatter={(value, name, props) => [props.payload.statusDisplay, 'Statut']} labelFormatter={(label) => `Date: ${label}`} />
            <Bar dataKey="value" name="Statut d'exécution">
              {filteredData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={statusChartColor(entry.status)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Évolution de la Durée</h3>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={filteredData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis dataKey="date" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} label={{ value: 'Durée (s)', angle: -90, position: 'insideLeft' }} />
            <Tooltip formatter={(value) => [`${value}s`, 'Durée']} labelFormatter={(label) => `Date: ${label}`} />
            <Area type="monotone" dataKey="durationSeconds" stroke="#3b82f6" strokeWidth={2} fill="url(#colorDuration)" name="Durée d'exécution" />
            <defs>
              <linearGradient id="colorDuration" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1} />
              </linearGradient>
            </defs>
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Détails par Build</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Build</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Durée</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredData.map((entry, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(entry.dateTime).toLocaleString('fr-FR', { 
                      year: 'numeric', 
                      month: '2-digit', 
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{entry.buildId}</td>
                  <td className="px-6 py-4 whitespace-nowrap"><StatusBadge status={entry.status} /></td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{entry.duration}s</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {entry.logUrl && (
                      <a href={entry.logUrl} className="text-blue-600 hover:text-blue-800 flex items-center gap-1" target="_blank" rel="noreferrer">
                        <ExternalLink className="w-3 h-3" />
                        Voir Log
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Composant pour afficher les détails d'une pipeline ReadyAPI dans le contexte Data Preparation
const ReadyApiPipelineView = ({ pipelineId, buildId, navigate }) => {
  const [builds, setBuilds] = useState([]);
  const [testSuites, setTestSuites] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('suites');
  const [currentBuildId, setCurrentBuildId] = useState(buildId);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Si pas de buildId, récupérer le dernier build
        let effectiveBuildId = currentBuildId;
        if (!effectiveBuildId) {
          const buildsData = await azureDevOpsService.getRecentBuildsByPipeline(pipelineId);
          const buildsList = buildsData[`pipeline-${pipelineId}`] || [];
          if (buildsList.length > 0) {
            effectiveBuildId = buildsList[0].buildId;
            setCurrentBuildId(effectiveBuildId);
          } else {
            throw new Error('Aucun build trouvé');
          }
        }

        // Charger toutes les données en parallèle
        const [testSuitesData, buildsData, trendBuilds] = await Promise.all([
          azureDevOpsService.getTestSuitesForBuild(effectiveBuildId),
          azureDevOpsService.getRecentBuildsByPipeline(pipelineId),
          azureDevOpsService.getTrendDataFromBuilds(pipelineId)
        ]);

        const suitesArray = testSuitesData[`build-${effectiveBuildId.replace('build-', '')}`] || [];
        setTestSuites(suitesArray);
        setBuilds(buildsData);
        setTrendData(trendBuilds);
      } catch (err) {
        console.error('Erreur chargement pipeline ReadyAPI:', err);
        setError('Impossible de charger les données de la pipeline');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [pipelineId, currentBuildId]);

  if (loading) return <div className="flex justify-center items-center h-64">Chargement...</div>;
  if (error) return <div className="text-red-600 text-center">{error}</div>;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{currentBuildId}</h2>
            <p className="text-gray-600">Pipeline ReadyAPI - Data Preparation</p>
          </div>
          <button onClick={() => navigate('/')} className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700">
            ← Retour
          </button>
        </div>

        {/* Statistiques du build actuel */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Test Suites</span>
              <span className="text-lg font-bold text-blue-600">{testSuites.length}</span>
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Tests Passés</span>
              <span className="text-lg font-bold text-green-600">{testSuites.reduce((sum, s) => sum + s.passed, 0)}</span>
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Tests Échoués</span>
              <span className="text-lg font-bold text-red-600">{testSuites.reduce((sum, s) => sum + s.failed, 0)}</span>
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Tests Ignorés</span>
              <span className="text-lg font-bold text-gray-500">{testSuites.reduce((sum, s) => sum + (s.skipped || 0), 0)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('suites')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'suites'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Test Suites
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'history'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Historique Builds
            </button>
            <button
              onClick={() => setActiveTab('table')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'table'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Vue Tableau
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* Onglet Test Suites */}
          {activeTab === 'suites' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {testSuites.map(suite => {
                const successRate = computeSuccessRate(suite.passed, suite.totalTests, suite.skipped);
                return (
                <div key={suite.runId} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900">{suite.name}</h4>
                    <SuccessRateBadge successRate={successRate} />
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total:</span>
                      <span className="font-medium">{suite.totalTests} tests</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Passés:</span>
                      <span className="font-medium text-green-600">{suite.passed}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Échoués:</span>
                      <span className="font-medium text-red-600">{suite.failed}</span>
                    </div>
                    {suite.skipped > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Ignorés:</span>
                        <span className="font-medium text-gray-500">{suite.skipped}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Durée:</span>
                      <span className="font-medium">{formatDuration(suite.duration)}</span>
                    </div>
                  </div>

                  <button
                    onClick={() => navigate(`/pipeline/${pipelineId}/build/${currentBuildId}/run/${suite.runId}/testsuite/${encodeURIComponent(suite.name)}`)}
                    className="w-full mt-3 bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700 transition-colors"
                  >
                    Voir Détails
                  </button>
                </div>
              );
              })}
            </div>
          )}

          {/* Onglet Historique des Builds */}
          {activeTab === 'history' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Historique des 5 derniers builds</h3>
              <div className="divide-y divide-gray-200">
                {builds.map(build => (
                  <div key={build.buildId} className={`p-6 hover:bg-gray-50 transition-colors ${
                    build.buildId === currentBuildId ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <StatusBadge status={build.status} />
                        <div>
                          <h4 className="font-medium text-gray-900">
                            {build.buildId}
                            {build.buildId === currentBuildId && (
                              <span className="ml-2 text-sm text-blue-600 font-normal">(Actuel)</span>
                            )}
                          </h4>
                          <p className="text-sm text-gray-600">{formatDate(build.date)}</p>
                        </div>
                      </div>

                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Tests:</span>
                        <span className="font-medium ml-2">
                          <span className="text-green-600">{build.passedTests}</span> / 
                          <span className="text-red-600">{build.failedTests}</span> / 
                          <span className="text-gray-900">{build.totalTests}</span>
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-4">
                        <span className="text-sm text-gray-600">{formatDuration(build.duration)}</span>
                        {build.buildId !== currentBuildId && (
                          <button
                            onClick={() => {
                              setCurrentBuildId(build.buildId);
                              navigate(`/pipeline/${pipelineId}/build/${build.buildId}`);
                            }}
                            className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors"
                          >
                            Voir ce Build
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Graphique d'évolution */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Évolution des Statuts</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="success" stroke="#10b981" strokeWidth={2} name="Succès" />
                    <Line type="monotone" dataKey="failed" stroke="#ef4444" strokeWidth={2} name="Échecs" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Onglet Vue Tableau */}
          {activeTab === 'table' && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Suite</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nb Tests</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Passés</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Échoués</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ignorés</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Durée</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {testSuites.map(suite => (
                    <tr key={suite.runId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">{suite.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-900">{suite.totalTests}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-green-600 font-medium">{suite.passed}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-red-600 font-medium">{suite.failed}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-500 font-medium">{suite.skipped || 0}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-900">{formatDuration(suite.duration)}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => navigate(`/pipeline/${pipelineId}/build/${currentBuildId}/run/${suite.runId}/testsuite/${encodeURIComponent(suite.name)}`)}
                          className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors"
                        >
                          Détails
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Dashboard Data Preparation
const DataPreparationDashboard = ({ navigate }) => {
  const [testSuites, setTestSuites] = useState([]);
  const [readyApiPipeline, setReadyApiPipeline] = useState(null);

  // Builds interrogés pour la synthèse des tests ignorés : les suites RF et, s'il existe,
  // le build ReadyAPI. Le panneau se charge lui-même à partir de cette liste.
  const skippedBuilds = useMemo(() => {
    const ids = testSuites.map(s => s.buildId).filter(Boolean);
    if (readyApiPipeline?.lastBuildId) ids.push(readyApiPipeline.lastBuildId);
    return [...new Set(ids)].map(buildId => ({ buildId }));
  }, [testSuites, readyApiPipeline]);
  const [trendData, setTrendData] = useState([]);
  const [trendDays, setTrendDays] = useState(7);
  const [trendLoading, setTrendLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [runningPipelines, setRunningPipelines] = useState(new Map());
  const [showFailingOnly, setShowFailingOnly] = useState(false);
  const [triggerLoading, setTriggerLoading] = useState(new Set());
  const [showDashSuiteAnalysis, setShowDashSuiteAnalysis] = useState(false);
  const [dashSuiteAnalysisResult, setDashSuiteAnalysisResult] = useState(null);
  const [selectedTrendDate, setSelectedTrendDate] = useState(null);
  const [isDashSuiteAnalyzing, setIsDashSuiteAnalyzing] = useState(false);
  const [dashSuiteAnalysisError, setDashSuiteAnalysisError] = useState(null);
  const [selectedDashSuite, setSelectedDashSuite] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(() => {
    try { return localStorage.getItem('dpVersion') || '26.1'; } catch { return '26.1'; }
  });
  useEffect(() => { try { localStorage.setItem('activeVersion', selectedVersion); } catch {} }, [selectedVersion]);
  useEffect(() => { setSelectedTrendDate(null); }, [selectedVersion]);
  const activeConfig = VERSION_CONFIGS_DP[selectedVersion] || VERSION_CONFIGS_DP['26.1'];

  const handleAnalyzeSuiteCard = async (suite, force = false) => {
    setSelectedDashSuite(suite);
    setShowDashSuiteAnalysis(true);
    if (!force) {
      const cached = getCachedSuiteAnalysis(suite.name, suite.runId);
      if (cached) {
        setDashSuiteAnalysisResult({ ...cached.result, _fromCache: true, _cachedAt: cached.savedAt });
        setDashSuiteAnalysisError(null);
        setIsDashSuiteAnalyzing(false);
        return;
      }
    }
    setIsDashSuiteAnalyzing(true);
    setDashSuiteAnalysisError(null);
    setDashSuiteAnalysisResult(null);
    try {
      const testCases = await azureDevOpsService.getTestCasesForRun(suite.runId);
      const result = await analyzeSuite(suite.name, testCases, testCases.length);
      writeSuiteAnalysisCache(suite.name, suite.runId, result);
      setDashSuiteAnalysisResult(result);
    } catch (err) {
      setDashSuiteAnalysisError(err.message);
    } finally {
      setIsDashSuiteAnalyzing(false);
    }
  };

  const handleAnalyzeReadyApiCard = async (force = false) => {
    const suiteName = readyApiPipeline?.name || 'ReadyAPI';
    setSelectedDashSuite({ name: suiteName, failed: readyApiPipeline?.failedTests || 0 });
    setShowDashSuiteAnalysis(true);
    if (!force) {
      const cached = getCachedSuiteAnalysis(suiteName, readyApiPipeline?.lastBuildId);
      if (cached) {
        setDashSuiteAnalysisResult({ ...cached.result, _fromCache: true, _cachedAt: cached.savedAt });
        setDashSuiteAnalysisError(null);
        setIsDashSuiteAnalyzing(false);
        return;
      }
    }
    setIsDashSuiteAnalyzing(true);
    setDashSuiteAnalysisError(null);
    setDashSuiteAnalysisResult(null);
    try {
      const runs = await azureDevOpsService.getTestRunsByBuildId(readyApiPipeline.lastBuildId);
      const allResults = [];
      for (const run of runs) {
        const results = await azureDevOpsService.getTestRunResults(run.id);
        allResults.push(...results.map(r => ({ ...r, _runId: run.id })));
      }
      const testCases = allResults.map(r => ({
        id: String(r.id || ''),
        name: r.testCaseTitle || r.automatedTestName || 'Test inconnu',
        status: isSkippedStatus(r.outcome) ? 'skipped' : (r.outcome || '').toLowerCase() === 'passed' ? 'passed' : 'failed',
        error: (r.stackTrace || r.errorMessage || '').slice(0, 800),
        logRunId: r._runId || null,
        logTestId: r.id || null,
      }));
      const result = await analyzeSuite(suiteName, testCases, testCases.length);
      writeSuiteAnalysisCache(suiteName, readyApiPipeline?.lastBuildId, result);
      setDashSuiteAnalysisResult(result);
    } catch (err) {
      setDashSuiteAnalysisError(err.message);
    } finally {
      setIsDashSuiteAnalyzing(false);
    }
  };

  const handleTriggerPipeline = async (pipelineId) => {
    setTriggerLoading(prev => new Set([...prev, pipelineId]));
    try {
      await azureDevOpsService.triggerPipeline(pipelineId);
    } catch (err) {
      console.error('Failed to trigger pipeline:', err);
    } finally {
      setTriggerLoading(prev => { const s = new Set(prev); s.delete(pipelineId); return s; });
    }
  };

  const [globalStats, setGlobalStats] = useState({
    totalTests: 0,
    totalPassed: 0,
    totalFailed: 0,
    totalSkipped: 0,
    averageSuccessRate: 0,
    averageDuration: 0
  });

  useEffect(() => {
    const fetchData = async () => {
      const cfg = VERSION_CONFIGS_DP[selectedVersion] || VERSION_CONFIGS_DP['26.1'];
      try {
        setLoading(true);
        setError(null);
        setReadyApiPipeline(null);

        // Récupérer les données RobotFramework
        const robotSuitesPromises = cfg.filter(c => c.type === 'robotframework').map(async pipelineCfg => {
          try {
            let latestBuild;
            if (selectedTrendDate) {
              const builds = await azureDevOpsService.getBuildsBySessionKey(pipelineCfg.pipelineId, selectedTrendDate);
              if (!builds.length) return null;
              latestBuild = builds[0];
            } else {
              const buildsData = await azureDevOpsService.getRecentBuildsByPipeline(pipelineCfg.pipelineId);
              const builds = buildsData[`pipeline-${pipelineCfg.pipelineId}`] || [];
              if (!builds.length) return null;
              latestBuild = builds[0];
            }
            const testSuitesData = await azureDevOpsService.getTestSuitesForBuild(latestBuild.buildId);
            const allSuites = testSuitesData[`build-${latestBuild.buildId.replace('build-', '')}`] || [];
            const dpSuite = allSuites.find(s => s.name === pipelineCfg.testSuiteName || (pipelineCfg.testSuiteFilter && s.name.toLowerCase().includes(pipelineCfg.testSuiteFilter.toLowerCase())));
            if (dpSuite) {
              return {
                ...dpSuite,
                pipelineId: pipelineCfg.pipelineId,
                pipelineName: pipelineCfg.pipelineName,
                buildId: latestBuild.buildId,
                executionDate: latestBuild.date,
                type: 'robotframework'
              };
            }
            return null;
          } catch (e) {
            console.error('Erreur pipeline', pipelineCfg.pipelineId, e);
            return null;
          }
        });

        const robotSuites = (await Promise.all(robotSuitesPromises)).filter(s => s);
        setTestSuites(robotSuites);

        const runMap = new Map();
        await Promise.all(cfg.filter(c => c.type === 'robotframework').map(async pipelineCfg => {
          const runningBuild = await azureDevOpsService.hasRunningBuild(pipelineCfg.pipelineId);
          if (runningBuild) runMap.set(pipelineCfg.pipelineId, runningBuild);
        }));
        setRunningPipelines(runMap);

        // Récupérer les données ReadyAPI
        const readyApiConfig = cfg.find(c => c.type === 'readyapi');
        let readyApiData = null;
        if (readyApiConfig) {
          try {
            const buildsData = selectedTrendDate
              ? await azureDevOpsService.getBuildsDataForSession([{ id: readyApiConfig.pipelineId, name: readyApiConfig.pipelineName }], selectedTrendDate)
              : await azureDevOpsService.getBuildsData([{ id: readyApiConfig.pipelineId, name: readyApiConfig.pipelineName }]);
            readyApiData = buildsData.find(p => p.id === readyApiConfig.pipelineId);
            if (readyApiData) {
              setReadyApiPipeline({
                ...readyApiData,
                testEnvironment: readyApiData.testEnvironment || readyApiConfig.testEnvironment,
                type: 'readyapi'
              });
            }
          } catch (e) {
            console.error('Erreur ReadyAPI pipeline', e);
          }
        }

        // Calculer les statistiques globales (RobotFramework + ReadyAPI)
        let totalTests = 0;
        let totalPassed = 0;
        let totalFailed = 0;
        let totalSkipped = 0;
        let totalDuration = 0;
        let count = 0;

        robotSuites.forEach(suite => {
          totalTests += suite.totalTests || 0;
          totalPassed += suite.passed || 0;
          totalFailed += suite.failed || 0;
          totalSkipped += suite.skipped || 0;
          totalDuration += suite.duration || 0;
          count++;
        });

        if (readyApiData) {
          totalTests += readyApiData.totalTests || 0;
          totalPassed += readyApiData.passedTests || 0;
          totalFailed += readyApiData.failedTests || 0;
          totalSkipped += readyApiData.skippedTests || 0;
          totalDuration += readyApiData.totalDuration || 0;
          count++;
        }

        setGlobalStats({
          totalTests,
          totalPassed,
          totalFailed,
          totalSkipped,
          averageSuccessRate: computeSuccessRate(totalPassed, totalTests, totalSkipped),
          averageDuration: count > 0 ? totalDuration / count : 0
        });

      } catch (err) {
        console.error('Erreur chargement données DP:', err);
        setError('Impossible de charger les données Data Preparation');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedVersion, selectedTrendDate]);

  useEffect(() => {
    const fetchTrend = async () => {
      const cfg = VERSION_CONFIGS_DP[selectedVersion] || VERSION_CONFIGS_DP['26.1'];
      setTrendLoading(true);
      try {
        const robotConfigs = cfg.filter(c => c.type === 'robotframework');
        const readyApiConfigs = cfg.filter(c => c.type === 'readyapi');
        const [robotData, readyData] = await Promise.all([
          robotConfigs.length > 0
            ? azureDevOpsService.getTrendDataBySessions(robotConfigs.map(c => c.pipelineId), trendDays, robotConfigs.flatMap(c => c.testSuiteFilter ? [c.testSuiteName, c.testSuiteFilter] : [c.testSuiteName]))
            : Promise.resolve([]),
          readyApiConfigs.length > 0
            ? azureDevOpsService.getTrendDataBySessions(readyApiConfigs.map(c => c.pipelineId), trendDays, null)
            : Promise.resolve([])
        ]);
        const map = {};
        for (const item of [...robotData, ...readyData]) {
          if (map[item.date]) {
            map[item.date].success += item.success;
            map[item.date].failed += item.failed;
            map[item.date].skipped += item.skipped;
            map[item.date].total += item.total;
            Object.assign(map[item.date].suites, item.suites);
          } else {
            map[item.date] = { ...item, suites: { ...item.suites } };
          }
        }
        setTrendData(Object.values(map).sort((a, b) => (a._key < b._key ? -1 : a._key > b._key ? 1 : 0)));
      } catch (e) {
        console.error('Erreur tendance DP:', e);
      } finally {
        setTrendLoading(false);
      }
    };
    fetchTrend();
  }, [trendDays, selectedVersion]);

  if (loading) return <div className="min-h-screen bg-gray-100 flex items-center justify-center"><div className="text-center"><div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div><p className="text-gray-600">Chargement Data Preparation...</p></div></div>;
  if (error) return <div className="min-h-screen bg-gray-100 flex items-center justify-center"><div className="text-center text-red-600"><XCircle className="w-12 h-12 mx-auto mb-4"/><p>{error}</p></div></div>;

  const rateColor = (rate) => rate >= 90 ? 'bg-green-500' : rate >= 70 ? 'bg-yellow-400' : 'bg-red-500';

  // Variation nette du nombre de tests sur la période, calculée suite par suite (cf. computeNetTestDelta).
  const { first: trendFirstTotal, last: trendLastTotal, delta: trendAddedTests } = computeNetTestDelta(trendData);
  const trendAddedTextColor = trendAddedTests > 0 ? 'text-green-600' : trendAddedTests < 0 ? 'text-orange-600' : 'text-gray-500';

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Data Preparation</h1>
              <p className="text-gray-600 mt-1">Tests RobotFramework et ReadyAPI</p>
              <div className="flex items-center gap-2 mt-3">
                <label className="text-sm text-gray-600">Version</label>
                <select
                  value={selectedVersion}
                  onChange={e => { setSelectedVersion(e.target.value); localStorage.setItem('dpVersion', e.target.value); }}
                  className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                >
                  <option value="26.2">26.2</option>
                  <option value="26.1">26.1</option>
                  <option value="25.2">25.2</option>
                  <option value="25.1">25.1</option>
                </select>
              </div>
            </div>
            <TestTubes className="w-12 h-12 text-blue-600" />
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          <div className="flex justify-end mb-2">
            <button
              onClick={() => window.location.reload()}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:text-blue-600 transition-colors shadow-sm"
            >
              <RefreshCcw className="w-3.5 h-3.5" />
              Rafraîchir
            </button>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              { label: 'Total Tests', value: globalStats.totalTests, color: 'text-gray-900', icon: <TestTubes className="w-8 h-8 text-gray-600" /> },
              { label: 'Tests Passés', value: globalStats.totalPassed, color: 'text-green-600', icon: <CheckCircle className="w-8 h-8 text-green-600" /> },
              { label: 'Tests Échoués', value: globalStats.totalFailed, color: 'text-red-600', icon: <XCircle className="w-8 h-8 text-red-600" /> },
              { label: 'Tests Ignorés', value: globalStats.totalSkipped, color: 'text-gray-500', icon: <Clock className="w-8 h-8 text-gray-400" /> },
              { label: 'Taux de Réussite', value: (globalStats.averageSuccessRate.toFixed(1) + '%'), color: 'text-blue-600', icon: <TrendingUp className="w-8 h-8 text-blue-600" /> }
            ].map(card => (
              <div key={card.label} className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-600">{card.label}</p>
                    <p className={`text-2xl font-bold ${card.color}`}>{card.value}</p>
                  </div>
                  {card.icon}
                </div>
              </div>
            ))}
          </div>

          {globalStats.totalSkipped > 0 && (
            <SkippedTestsPanel builds={skippedBuilds} expectedTotal={globalStats.totalSkipped} />
          )}

          <AllSuitesVerifiedBanner
            suites={readyApiPipeline && !readyApiPipeline.hasNoResults
              ? [...testSuites, { pipelineId: readyApiPipeline.id, buildId: readyApiPipeline.lastBuildId, name: readyApiPipeline.name, failed: readyApiPipeline.failedTests, totalTests: readyApiPipeline.totalTests, executionDate: readyApiPipeline.executionDate }]
              : testSuites}
            version={selectedVersion}
          />

          {/* Pipelines en cours */}
          {(runningPipelines.size > 0 || readyApiPipeline?.isRunning) && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                <span className="text-sm font-semibold text-blue-800">Pipelines en cours d'exécution</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {activeConfig.filter(cfg => runningPipelines.has(cfg.pipelineId)).map(cfg => (
                  <a
                    key={cfg.pipelineId}
                    href={runningPipelines.get(cfg.pipelineId).url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300 hover:bg-blue-200 transition-colors"
                  >
                    <Play className="w-3 h-3" />{cfg.pipelineName}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                ))}
                {readyApiPipeline?.isRunning && (
                  <a
                    href={`https://dev.azure.com/${azureDevOpsService.organization}/${azureDevOpsService.project}/_build/results?buildId=${readyApiPipeline.lastBuildId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300 hover:bg-blue-200 transition-colors"
                  >
                    <Play className="w-3 h-3" />{readyApiPipeline.name} (ReadyAPI)
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-gray-600 font-medium">
              {testSuites.length + (readyApiPipeline ? 1 : 0)} suite{(testSuites.length + (readyApiPipeline ? 1 : 0)) !== 1 ? 's' : ''}
            </span>
            <button
              onClick={() => setShowFailingOnly(!showFailingOnly)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${showFailingOnly ? 'bg-red-50 text-red-700 border-red-200' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
            >
              <XCircle className="w-3.5 h-3.5" />
              {showFailingOnly ? 'Tous les pipelines' : 'Pipelines en échec'}
            </button>
          </div>

          {/* Pipelines Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {/* RobotFramework Suites */}
            {testSuites.filter(suite => !showFailingOnly || suite.failed > 0).map(suite => {
              const successRate = computeSuccessRate(suite.passed, suite.totalTests, suite.skipped);
              const isRunning = runningPipelines.has(suite.pipelineId);
              return (
              <div key={`${suite.pipelineId}-${suite.runId}`} className={`rounded-lg shadow p-6 ${isRunning ? 'bg-blue-50 border border-blue-200' : 'bg-white'}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="min-h-[5rem]">
                    <h3 className="text-lg font-semibold text-gray-900">{suite.pipelineName}</h3>
                    <p className="text-sm text-gray-600 mt-1">{suite.name}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {isRunning && (
                      <a
                        href={runningPipelines.get(suite.pipelineId).url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300 hover:bg-blue-200 transition-colors"
                        title="Voir la pipeline en cours sur Azure DevOps"
                      >
                        <Loader2 className="w-3 h-3 animate-spin" />En cours
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    <button
                      onClick={() => handleTriggerPipeline(suite.pipelineId)}
                      disabled={triggerLoading.has(suite.pipelineId)}
                      className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 border border-gray-200 disabled:opacity-50 transition-colors"
                      title="Lancer le pipeline"
                    >
                      {triggerLoading.has(suite.pipelineId) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                    </button>
                    <SuccessRateBadge successRate={successRate} />
                  </div>
                </div>

                <div className="space-y-3">
                  {suite.executionDate && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 flex items-center gap-1"><Calendar className="w-4 h-4" />Date:</span>
                      <span className="font-medium">
                        <a
                          href={`https://dev.azure.com/${azureDevOpsService.organization}/${azureDevOpsService.project}/_build/results?buildId=${suite.buildId?.replace('build-', '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
                          title="Voir le build sur Azure DevOps"
                        >
                          {formatDate(suite.executionDate)}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                        {suite.executionDate && (() => {
                          const days = Math.floor((Date.now() - new Date(suite.executionDate).getTime()) / 86400000);
                          if (days === 0) return null;
                          return <span className={`ml-1 text-xs ${days > 5 ? 'text-orange-500' : 'text-gray-400'}`}>({days === 1 ? 'hier' : `il y a ${days}j`})</span>;
                        })()}
                      </span>
                    </div>
                  )}
                  {suite.testEnvironment && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Environnement de test:</span>
                      <span className="font-medium">{suite.testEnvironment}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Build:</span>
                    <span className="font-medium">{suite.buildId}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Tests:</span>
                    <span className="font-medium">
                      <span className="text-green-600">{suite.passed}</span> / 
                      <span className="text-red-600"> {suite.failed}</span> / 
                      {suite.skipped > 0 && <><span className="text-gray-500"> {suite.skipped}</span> / </>}
                      <span className="text-gray-900"> {suite.totalTests}</span>
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 flex items-center gap-1"><Clock className="w-4 h-4" />Durée:</span>
                    <span className="font-medium">{formatDuration(suite.duration)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Taux de réussite:</span>
                    <span className="font-medium">{successRate.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div className={`${rateColor(successRate)} h-2 rounded-full`} style={{ width: `${successRate}%` }}></div>
                  </div>
                  {suite.failed > 0 && (
                    <button
                      onClick={() => handleAnalyzeSuiteCard(suite)}
                      className="w-full mt-2 flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md text-sm font-medium transition"
                    >
                      🤖 Analyser les échecs ({suite.failed})
                    </button>
                  )}
                  <button
                    onClick={() => navigate(`/pipeline/${suite.pipelineId}/build/${suite.buildId}/run/${suite.runId}/testsuite/${encodeURIComponent(suite.name)}`)}
                    className="w-full mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <Eye className="w-4 h-4" />
                    Voir Détails
                  </button>
                  <div className="flex gap-2 mt-2">
                    <RobotReportButton buildId={suite.buildId} kind="report" className="flex-1 justify-center" />
                    <RobotReportButton buildId={suite.buildId} kind="log" className="flex-1 justify-center" />
                  </div>
                  <SuiteVerifyButton pipelineId={suite.pipelineId} buildId={suite.buildId} runId={suite.runId} suiteName={suite.name} failed={suite.failed} totalTests={suite.totalTests} executionDate={suite.executionDate} version={selectedVersion} />
                </div>
              </div>
              );
            })}

            {/* RF Pipelines en cours sans résultats */}
            {activeConfig.filter(cfg =>
              cfg.type === 'robotframework' &&
              runningPipelines.has(cfg.pipelineId) &&
              !testSuites.some(s => s.pipelineId === cfg.pipelineId)
            ).map(cfg => (
              <div key={`placeholder-${cfg.pipelineId}`} className="rounded-lg shadow p-6 bg-blue-50 border border-blue-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="min-h-[5rem]">
                    <h3 className="text-lg font-semibold text-gray-900">{cfg.pipelineName}</h3>
                    <p className="text-sm text-gray-600 mt-1">{cfg.testSuiteName}</p>
                  </div>
                  <a
                    href={runningPipelines.get(cfg.pipelineId).url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300 hover:bg-blue-200 transition-colors"
                    title="Voir la pipeline en cours sur Azure DevOps"
                  >
                    <Loader2 className="w-3 h-3 animate-spin" />En cours
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <div className="py-6 text-center text-sm text-blue-600 italic">
                  Exécution en cours, résultats pas encore disponibles...
                </div>
                <button
                  disabled
                  className="w-full mt-2 px-4 py-2 rounded-md flex items-center justify-center gap-2 bg-gray-100 text-gray-400 cursor-not-allowed"
                  title="Pas de résultats disponibles"
                >
                  <Eye className="w-4 h-4" />
                  Voir Détails
                </button>
              </div>
            ))}

            {/* ReadyAPI Pipeline */}
            {readyApiPipeline && (
              <div className={`rounded-lg shadow p-6 ${readyApiPipeline.isRunning ? 'bg-blue-50 border border-blue-200' : 'bg-white'}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="min-h-[5rem]">
                    <h3 className="text-lg font-semibold text-gray-900">{readyApiPipeline.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">ReadyAPI Tests</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {readyApiPipeline.isRunning && (
                      <a
                        href={`https://dev.azure.com/${azureDevOpsService.organization}/${azureDevOpsService.project}/_build/results?buildId=${readyApiPipeline.lastBuildId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300 hover:bg-blue-200 transition-colors"
                        title="Voir la pipeline en cours sur Azure DevOps"
                      >
                        <Loader2 className="w-3 h-3 animate-spin" />En cours
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    <button
                      onClick={() => handleTriggerPipeline(readyApiPipeline.id)}
                      disabled={triggerLoading.has(readyApiPipeline.id)}
                      className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 border border-gray-200 disabled:opacity-50 transition-colors"
                      title="Lancer le pipeline"
                    >
                      {triggerLoading.has(readyApiPipeline.id) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                    </button>
                    <SuccessRateBadge successRate={readyApiPipeline.successRate} />
                  </div>
                </div>
                
                <div className="space-y-3">
                  {readyApiPipeline.executionDate && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 flex items-center gap-1"><Calendar className="w-4 h-4" />Date:</span>
                      <span className="font-medium">
                        <a
                          href={`https://dev.azure.com/${azureDevOpsService.organization}/${azureDevOpsService.project}/_build/results?buildId=${readyApiPipeline.lastBuildId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
                          title="Voir le build sur Azure DevOps"
                        >
                          {formatDate(readyApiPipeline.executionDate)}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                        {readyApiPipeline.executionDate && (() => {
                          const days = Math.floor((Date.now() - new Date(readyApiPipeline.executionDate).getTime()) / 86400000);
                          if (days === 0) return null;
                          return <span className={`ml-1 text-xs ${days > 5 ? 'text-orange-500' : 'text-gray-400'}`}>({days === 1 ? 'hier' : `il y a ${days}j`})</span>;
                        })()}
                      </span>
                    </div>
                  )}
                  {readyApiPipeline.testEnvironment && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Environnement de test:</span>
                      <span className="font-medium">{readyApiPipeline.testEnvironment}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Build:</span>
                    <span className="font-medium">{readyApiPipeline.lastBuildId}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Tests:</span>
                    <span className="font-medium">
                      <span className="text-green-600">{readyApiPipeline.passedTests}</span> / 
                      <span className="text-red-600"> {readyApiPipeline.failedTests}</span> / 
                      <span className="text-gray-900"> {readyApiPipeline.totalTests}</span>
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 flex items-center gap-1"><Clock className="w-4 h-4" />Durée:</span>
                    <span className="font-medium">{formatDuration(readyApiPipeline.totalDuration)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Taux de réussite:</span>
                    <span className="font-medium">{readyApiPipeline.successRate || 0}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div className={`${rateColor(readyApiPipeline.successRate || 0)} h-2 rounded-full`} style={{ width: `${readyApiPipeline.successRate || 0}%` }}></div>
                  </div>
                  {readyApiPipeline.failedTests > 0 && (
                    <button
                      onClick={handleAnalyzeReadyApiCard}
                      className="w-full mt-2 flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md text-sm font-medium transition"
                    >
                      🤖 Analyser les échecs ({readyApiPipeline.failedTests})
                    </button>
                  )}
                  <button
                    onClick={() => navigate(`/pipeline/${readyApiPipeline.id}`)}
                    disabled={readyApiPipeline.hasNoResults}
                    className={`w-full mt-4 px-4 py-2 rounded-md flex items-center justify-center gap-2 transition-colors ${readyApiPipeline.hasNoResults ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                    title={readyApiPipeline.hasNoResults ? 'Pas de résultats disponibles' : undefined}
                  >
                    <Eye className="w-4 h-4" />
                    Voir Test Suites
                  </button>
                  {!readyApiPipeline.hasNoResults && (
                    <SuiteVerifyButton pipelineId={readyApiPipeline.id} buildId={readyApiPipeline.lastBuildId} suiteName={readyApiPipeline.name} failed={readyApiPipeline.failedTests} totalTests={readyApiPipeline.totalTests} executionDate={readyApiPipeline.executionDate} version={selectedVersion} />
                  )}
                </div>
              </div>
            )}
          </div>

          {testSuites.length === 0 && !readyApiPipeline && (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <TestTubes className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune donnée disponible</h3>
              <p className="text-gray-600">Les données Data Preparation ne sont pas disponibles pour le moment.</p>
            </div>
          )}

          {(trendLoading || trendData.length > 0) && (
            <div className="bg-white rounded-xl shadow-md p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-700">Évolution des Tests Data Preparation</span>
                  {selectedTrendDate && (
                    <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                      <Calendar className="w-3 h-3" />
                      {trendData.find(d => d._key === selectedTrendDate)?.date || selectedTrendDate}
                      <button onClick={() => setSelectedTrendDate(null)} className="ml-1 hover:text-blue-900">×</button>
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {!trendLoading && trendData.length > 1 && (
                    <span
                      className="mr-2 text-xs text-gray-500"
                      title={`${trendFirstTotal} → ${trendLastTotal} tests, suite par suite (1re → dernière exécution de la période) : variation nette, ajouts moins suppressions. Une suite vue une seule fois dans la période n'est pas comptée.`}
                    >
                      Tests ajoutés : <span className={`font-semibold ${trendAddedTextColor}`}>{trendAddedTests > 0 ? '+' : ''}{trendAddedTests}</span>
                    </span>
                  )}
                  {[7, 14, 21].map(d => (
                    <button key={d} onClick={() => setTrendDays(d)}
                      className={`px-2 py-0.5 text-xs rounded transition-colors ${trendDays === d ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200 border border-gray-200'}`}>
                      {d}j
                    </button>
                  ))}
                </div>
              </div>
              {!selectedTrendDate && <p className="text-xs text-gray-400 mb-1">Cliquez sur une barre pour voir les résultats de ce jour</p>}
              {trendLoading ? (
                <div className="h-52 flex items-center justify-center">
                  <div className="animate-spin h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full" />
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart
                    data={trendData}
                    margin={{ top: 4, right: 4, bottom: 40, left: 0 }}
                    style={{ cursor: 'pointer' }}
                    onClick={(data) => {
                      const key = data?.activePayload?.[0]?.payload?._key;
                      if (key) setSelectedTrendDate(prev => prev === key ? null : key);
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 9 }} interval={0} angle={-30} textAnchor="end" />
                    <YAxis tick={{ fontSize: 9 }} width={28} />
                    <Tooltip content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const entry = payload[0].payload;
                      const dayRate = computeSuccessRate(entry.success, entry.total, entry.skipped);
                      const dayRateColor = dayRate >= 90 ? 'text-green-600' : dayRate >= 70 ? 'text-yellow-600' : 'text-red-600';
                      return (
                        <div className="bg-white border border-gray-200 rounded shadow p-2 text-xs">
                          <p className="font-semibold">{entry.date}</p>
                          <p className="text-green-600">✓ {entry.success}</p>
                          <p className="text-red-500">✗ {entry.failed}</p>
                          <p className="text-gray-600">= {entry.total}</p>
                          <p className="text-gray-600">Taux : <span className={`font-semibold ${dayRateColor}`}>{dayRate.toFixed(1)}%</span></p>
                          <p className="text-gray-400 mt-1">Cliquez pour voir les résultats</p>
                        </div>
                      );
                    }} />
                    <Bar dataKey="success" stackId="a" fill="#10b981">
                      {trendData.map((entry, idx) => (
                        <Cell key={idx} fill={entry._key === selectedTrendDate ? '#059669' : '#10b981'} />
                      ))}
                    </Bar>
                    <Bar dataKey="failed" stackId="a" fill="#ef4444">
                      {trendData.map((entry, idx) => (
                        <Cell key={idx} fill={entry._key === selectedTrendDate ? '#b91c1c' : '#ef4444'} />
                      ))}
                    </Bar>
                    {trendData.length > 0 && (
                      <ReferenceLine
                        y={Math.max(...trendData.map(d => d.total))}
                        stroke="#94a3b8"
                        strokeDasharray="4 3"
                        label={{ value: `max ${Math.max(...trendData.map(d => d.total))}`, position: 'insideTopRight', fontSize: 9, fill: '#94a3b8' }}
                      />
                    )}
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          )}
        </div>
      </main>
      <SuiteAnalysisPanel
        open={showDashSuiteAnalysis}
        onClose={() => setShowDashSuiteAnalysis(false)}
        squadName={selectedDashSuite?.name || 'Suite'}
        failedCount={selectedDashSuite?.failed || 0}
        isLoading={isDashSuiteAnalyzing}
        error={dashSuiteAnalysisError}
        result={dashSuiteAnalysisResult}
        onAnalyze={() => { if (!selectedDashSuite) return; if (selectedDashSuite.runId) handleAnalyzeSuiteCard(selectedDashSuite, true); else handleAnalyzeReadyApiCard(true); }}
      />
    </div>
  );
};

const DataPreparationPage = () => {
  const { uiPath, params, navigate } = useRouter();
  const [pendingFilter, setPendingFilter] = useState('');
  const pipelineIds = DP_CONFIG.map(c => c.pipelineId);

  const renderContent = () => {
    if (uiPath === '/') return <DataPreparationDashboard navigate={navigate} />;
    
    if (uiPath.startsWith('/testcase/') && params.testcaseName && params.pipelineId) {
      // Déterminer le type de test case selon le pipeline
      if (READYAPI_PIPELINE_IDS.has(params.pipelineId)) {
        return <ReadyApiTestCaseHistory testcaseName={params.testcaseName} pipelineId={params.pipelineId} backPipelineId={params.backPipelineId} backBuildId={params.backBuildId} backRunId={params.backRunId} backTestSuiteName={params.backTestSuiteName} navigate={navigate} />;
      } else {
        return <RobotTestCaseHistory testcaseName={params.testcaseName} pipelineId={params.pipelineId} backPipelineId={params.backPipelineId} backBuildId={params.backBuildId} backRunId={params.backRunId} backTestSuiteName={params.backTestSuiteName} navigate={navigate} />;
      }
    }
    
    if (uiPath.startsWith('/pipeline/') && params.pipelineId) {
      // Si c'est une pipeline ReadyAPI (304)
      if (READYAPI_PIPELINE_IDS.has(params.pipelineId)) {
        // Si on a une test suite, afficher les détails de la test suite ReadyAPI
        if (params.runId && params.testSuiteName) {
          return <ReadyApiTestSuiteDetails key={`${params.pipelineId}-${params.runId}-${params.testSuiteName}`} pipelineId={params.pipelineId} buildId={params.buildId} runId={params.runId} testSuiteName={params.testSuiteName} navigate={navigate} initialSearchTerm={pendingFilter} />;
        }
        // Sinon afficher la vue pipeline ReadyAPI (avec ou sans buildId)
        return <ReadyApiPipelineView pipelineId={params.pipelineId} buildId={params.buildId} navigate={navigate} />;
      }
      
      // Si c'est une test suite RobotFramework
      if (params.runId && params.testSuiteName) {
        return <RobotTestSuiteDetails key={`${params.pipelineId}-${params.runId}-${params.testSuiteName}`} pipelineId={params.pipelineId} buildId={params.buildId} runId={params.runId} testSuiteName={params.testSuiteName} navigate={navigate} initialSearchTerm={pendingFilter} />;
      }
    }
    
    return <div className="text-red-600">Page non trouvée</div>;
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Toaster position="top-right" />
      <Navigation currentPath={uiPath} navigate={navigate} />
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
          <TestCaseSearchBar
            onSelect={async (result) => {
              setPendingFilter(result.title);
              try {
                const location = await azureDevOpsService.findTestCaseLocation(result.id, result.title, pipelineIds);
                if (location) {
                  navigate(`/pipeline/${location.pipelineId}/build/${location.buildId}/run/${location.runId}/testsuite/${encodeURIComponent(location.suiteName)}`);
                  return;
                }
              } catch (e) {
                console.warn('findTestCaseLocation échec:', e);
              }
              navigate('/');
            }}
            pendingFilter={pendingFilter}
            onClearFilter={() => setPendingFilter('')}
          />
        </div>
      </div>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {renderContent()}
      </main>
    </div>
  );
};

export default DataPreparationPage;
